import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { CellClickedEvent, CellValueChangedEvent, FilterChangedEvent } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react' // React Data Grid Component
import { Badge } from 'primereact/badge'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { InputSwitch } from 'primereact/inputswitch'
import { InputText } from 'primereact/inputtext'
import { OverlayPanel } from 'primereact/overlaypanel'
import { SplitButton } from 'primereact/splitbutton'
import { classNames } from 'primereact/utils'
import { toPng } from 'html-to-image'
import toast from 'react-hot-toast'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import {
  useGetHeadlineData,
  useGetCupProjectRunCoefficientData,
  useRecalculateCupRunCoefficientData,
  useGetCupRunCoefficientRecalculateStatus,
  useGetHeadlineDataByCase,
  useGetHeadlineDraftingInfo,
  useGetRunCase
} from '../../api/report-API'
import {
  AvailableFieldsForHeadlineData,
  CUP28States,
  KEY_DELIMITER,
  REF_GM_ID_2ND,
  TREE_LEVEL_CASE,
  TREE_LEVEL_PROJECT,
  TREE_LEVEL_RUN
} from '../../utils/constants'
import {
  attitudeSetListState,
  currentTeamState,
  currentUserState,
  themeState
} from '../../utils/state-atoms'
import { getAgGridTheme } from '../../utils/styling-constants'
import {
  annotateDraftingPairs,
  getDraftPairBadgeClass,
  DRAFT_PAIR_INDEX_FIELD,
  DRAFT_ROLE_FIELD
} from '../../utils/draftingPairs'
import { isDashboardOnlyRole, canMutate } from '../../utils/roles'
import mavAeroIcon from '../../assets/image/mav-aero_icon_32.png'
import { BarChartIcon, FilterIcon2 } from '../Icons'
import PostProcGallery from './PostProcGallery'
import CreateGifModal, { GifCaseOption } from './CreateGifModal'
import GifPlayerScreen from './GifPlayerScreen'
import ProjectRunSelector from './ProjectRunSelector'
import { ReportRunSelection } from '../../api/report-generator-API'

// AG Grid v35's Theming API doesn't ship the legacy `.ag-right-aligned-header` CSS rule,
// so header text is right-aligned by reversing the label's flex direction directly.
const RIGHT_ALIGNED_HEADER_CLASS =
  '[&_.ag-header-cell-label]:flex-row-reverse [&_.ag-header-cell-label]:pr-1'

type ContextMenuParams = {
  node?: any
  column?: any
  defaultItems?: any[]
}

const cellDataFormatter = cellValue => {
  if (!cellValue && cellValue !== 0) {
    return null
  }
  const num = Number(cellValue)
  if (isNaN(num)) {
    return cellValue
  }
  return num.toFixed(3)
}

let sortNumber = 0
const extraAvailableFields = {
  project: {
    headerName: 'Project',
    field: 'project',
    sortNumber: sortNumber++,
    width: 150
  },
  run: {
    headerName: 'Run',
    field: 'run',
    sortNumber: sortNumber++,
    width: 150
  },
  project_desc: {
    headerName: 'Project Desc',
    field: 'project_desc',
    sortNumber: sortNumber++,
    width: 150,
    formatter: (value, level) => {
      if (level === TREE_LEVEL_PROJECT) {
        return value || ''
      }
      return ''
    }
  },
  run_desc: {
    headerName: 'Run Desc',
    field: 'run_desc',
    sortNumber: sortNumber++,
    width: 150,
    formatter: (value, level) => {
      if (level === TREE_LEVEL_RUN) {
        return value || ''
      }
      return ''
    }
  },
  state_name: {
    headerName: 'State',
    field: 'state_name',
    sortNumber: sortNumber++,
    width: 150
  },
  baseline_run: {
    headerName: 'Baseline Run',
    field: 'baseline_run',
    sortNumber: sortNumber++,
    width: 150
  }
}

const availableFields = AvailableFieldsForHeadlineData

const HEADLINE_COLUMNS_VISIBILITY = 'headline-data:columns-visibility'
// Run-level Ref 2 (reference run) overrides keyed by run node id (project|run). The value is
// what the user typed: a run name or any case number belonging to the reference run.
const REF2_STORAGE_KEY_PREFIX = 'headline-data:ref2-overrides:v2'
// Case-level Ref 2 overrides keyed by case gm_id; the value is the reference case gm_id.
// These take precedence over the run's reference run.
const REF2_CASE_STORAGE_KEY_PREFIX = 'headline-data:ref2-case-overrides:v1'
// Marks whether a case's Ref 2 came from a manual case entry or its run's reference run.
const REF2_SOURCE = 'ref2_source'
const REF2_BASE_SAVED = 'ref2_base_saved'
const REF2_DELTA_FIELDS = [
  'delta_cd',
  'delta_cl',
  'delta_clf',
  'delta_clr',
  'delta_cs',
  'delta_csf',
  'delta_csr',
  'delta_pct_clf',
  'pct_change_LF_Heat_Transfer_Monitor_W',
  'pct_change_RF_Heat_Transfer_Monitor_W',
  'pct_change_LR_Heat_Transfer_Monitor_W',
  'pct_change_RR_Heat_Transfer_Monitor_W'
] as const

type Ref2Overrides = Record<string, string>

const getRef2StorageKey = (team?: string, prefix: string = REF2_STORAGE_KEY_PREFIX) => {
  const teamScope = team || 'unknown-team'
  return `${prefix}:${teamScope}`
}

const readRef2OverridesFromStorage = (
  team?: string,
  prefix: string = REF2_STORAGE_KEY_PREFIX
): Ref2Overrides => {
  try {
    const saved = localStorage.getItem(getRef2StorageKey(team, prefix))
    if (!saved) {
      return {}
    }

    const parsed = JSON.parse(saved)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {}
    }

    return Object.entries(parsed).reduce((acc, [gmId, ref2]) => {
      if (ref2 === null || ref2 === undefined || ref2 === '') {
        return acc
      }

      acc[String(gmId)] = String(ref2)
      return acc
    }, {} as Ref2Overrides)
  } catch (error) {
    console.error('Failed to read Ref2 overrides from storage:', error)
    return {}
  }
}

const writeRef2OverridesToStorage = (
  overrides: Ref2Overrides,
  team?: string,
  prefix: string = REF2_STORAGE_KEY_PREFIX
) => {
  try {
    localStorage.setItem(getRef2StorageKey(team, prefix), JSON.stringify(overrides))
  } catch (error) {
    console.error('Failed to write Ref2 overrides to storage:', error)
  }
}

// Per-session exclusions from run-level Δ AVG calculations. `rows` holds fully excluded cases
// (by gm_id); `cells` maps a gm_id to the specific metric fields excluded for that case. Stored
// in sessionStorage so exclusions persist for the session but reset when the browser closes.
const DELTA_AVG_EXCLUSION_KEY_PREFIX = 'headline-data:delta-avg-exclusions:v1'

type DeltaAvgExclusions = {
  rows: Set<string>
  cells: Map<string, Set<string>>
}

const getDeltaAvgExclusionKey = (team?: string) => {
  return `${DELTA_AVG_EXCLUSION_KEY_PREFIX}:${team || 'unknown-team'}`
}

const createEmptyDeltaAvgExclusions = (): DeltaAvgExclusions => ({
  rows: new Set<string>(),
  cells: new Map<string, Set<string>>()
})

const readDeltaAvgExclusionsFromStorage = (team?: string): DeltaAvgExclusions => {
  try {
    const saved = sessionStorage.getItem(getDeltaAvgExclusionKey(team))
    if (!saved) {
      return createEmptyDeltaAvgExclusions()
    }

    const parsed = JSON.parse(saved)
    const rows = new Set<string>(
      Array.isArray(parsed?.rows) ? parsed.rows.map((gmId: any) => String(gmId)) : []
    )
    const cells = new Map<string, Set<string>>()
    if (parsed?.cells && typeof parsed.cells === 'object') {
      Object.entries(parsed.cells).forEach(([gmId, fields]) => {
        if (Array.isArray(fields) && fields.length > 0) {
          cells.set(String(gmId), new Set(fields.map((field: any) => String(field))))
        }
      })
    }
    return { rows, cells }
  } catch (error) {
    console.error('Failed to read Δ AVG exclusions from storage:', error)
    return createEmptyDeltaAvgExclusions()
  }
}

const writeDeltaAvgExclusionsToStorage = (exclusions: DeltaAvgExclusions, team?: string) => {
  try {
    const cells: Record<string, string[]> = {}
    exclusions.cells.forEach((fields, gmId) => {
      if (fields.size > 0) {
        cells[gmId] = Array.from(fields)
      }
    })
    sessionStorage.setItem(
      getDeltaAvgExclusionKey(team),
      JSON.stringify({ rows: Array.from(exclusions.rows), cells })
    )
  } catch (error) {
    console.error('Failed to write Δ AVG exclusions to storage:', error)
  }
}

const isRowDeltaAvgExcluded = (exclusions: DeltaAvgExclusions, gmId: string) => {
  return !!gmId && exclusions.rows.has(gmId)
}

const isCellDeltaAvgExcluded = (exclusions: DeltaAvgExclusions, gmId: string, field: string) => {
  if (!gmId || !field) {
    return false
  }
  return exclusions.cells.get(gmId)?.has(field) ?? false
}

// Returns ((a - b) / b) * 100, or null when either side is missing or the reference is zero.
// Heat monitors are sparse for non-thermal runs, so a plain calculation would coerce null to
// 0 and show a bogus value; a zero reference would divide by zero.
const percentChangeOrNull = (a: any, b: any) => {
  if (a === null || a === undefined || b === null || b === undefined) {
    return null
  }
  const reference = Number(b)
  if (reference === 0) {
    return null
  }
  return ((Number(a) - reference) / reference) * 100
}

// Applies a Ref 2 reference case to a case record (or clears it when ref2Value is empty).
// The record's original deltas are backed up into `<field>_2` once, so clearing or switching
// references always starts from — and can restore — the original deltas.
const applyRef2ToCaseRecord = <T extends Record<string, any>>(
  record: T,
  ref2Value: string | null,
  referenceCase: Record<string, any> | null,
  source: 'case' | 'run' | null = null
): T => {
  const nextRecord: Record<string, any> = { ...record }

  if (!nextRecord[REF2_BASE_SAVED]) {
    REF2_DELTA_FIELDS.forEach(field => {
      nextRecord[`${field}_2`] = record[field]
    })
    nextRecord[REF2_BASE_SAVED] = true
  }

  if (!ref2Value) {
    REF2_DELTA_FIELDS.forEach(field => {
      nextRecord[field] = nextRecord[`${field}_2`]
    })
    nextRecord[REF_GM_ID_2ND] = null
    nextRecord[REF2_SOURCE] = null
    return nextRecord as T
  }

  nextRecord[REF_GM_ID_2ND] = String(ref2Value)
  nextRecord[REF2_SOURCE] = source

  if (!referenceCase) {
    REF2_DELTA_FIELDS.forEach(field => {
      nextRecord[field] = nextRecord[`${field}_2`]
    })
    return nextRecord as T
  }

  nextRecord['delta_cd'] = record['cd'] - referenceCase['cd']
  nextRecord['delta_cl'] = record['cl'] - referenceCase['cl']
  nextRecord['delta_clf'] = record['clf'] - referenceCase['clf']
  nextRecord['delta_clr'] = record['clr'] - referenceCase['clr']
  nextRecord['delta_cs'] = record['cs'] - referenceCase['cs']
  nextRecord['delta_csf'] = record['csf'] - referenceCase['csf']
  nextRecord['delta_csr'] = record['csr'] - referenceCase['csr']
  nextRecord['delta_pct_clf'] = record['pct_clf'] - referenceCase['pct_clf']
  nextRecord['pct_change_LF_Heat_Transfer_Monitor_W'] = percentChangeOrNull(
    record['LF_Heat_Transfer_Monitor_W'],
    referenceCase['LF_Heat_Transfer_Monitor_W']
  )
  nextRecord['pct_change_RF_Heat_Transfer_Monitor_W'] = percentChangeOrNull(
    record['RF_Heat_Transfer_Monitor_W'],
    referenceCase['RF_Heat_Transfer_Monitor_W']
  )
  nextRecord['pct_change_LR_Heat_Transfer_Monitor_W'] = percentChangeOrNull(
    record['LR_Heat_Transfer_Monitor_W'],
    referenceCase['LR_Heat_Transfer_Monitor_W']
  )
  nextRecord['pct_change_RR_Heat_Transfer_Monitor_W'] = percentChangeOrNull(
    record['RR_Heat_Transfer_Monitor_W'],
    referenceCase['RR_Heat_Transfer_Monitor_W']
  )

  return nextRecord as T
}

type TreeDataNode = {
  id: string
  parentId: string
  treeLevel: string
  data: Record<string, any>
  // children?: TreeDataNode[];
} & Record<string, any>

type ReferenceRun = {
  key: string
  casesByState: Map<string, Record<string, any>>
}

const ATTITUDE_STATE_INDEX_FIELDS = [
  'attitude_state_index',
  'attitudeStateIndex',
  'attitude_state_order',
  'state_set_index',
  'stateSetIndex',
  'state_index',
  'stateIndex',
  'state_order',
  'stateOrder',
  'order_number',
  'orderNumber'
]

const toFiniteNumber = (value: any) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const buildAttitudeStateOrderMap = (attitudeSets: any[]) => {
  const stateOrder = new Map<string, number>()

  attitudeSets?.forEach(attitudeSet => {
    attitudeSet?.attitudeStates?.forEach((state: any, index: number) => {
      const stateName = state?.name
      if (!stateName) {
        return
      }

      const sortIndex = toFiniteNumber(state?.order_number) ?? index + 1
      const key = String(stateName).toLowerCase()
      const existingIndex = stateOrder.get(key)
      if (existingIndex === undefined || sortIndex < existingIndex) {
        stateOrder.set(key, sortIndex)
      }
    })
  })

  return stateOrder
}

const getAttitudeStateSortIndex = (
  record: Record<string, any>,
  stateOrder: Map<string, number>
) => {
  for (const field of ATTITUDE_STATE_INDEX_FIELDS) {
    const index = toFiniteNumber(record[field])
    if (index !== null) {
      return index
    }
  }

  const stateName = record.state_name
  if (stateName !== null && stateName !== undefined) {
    const stateOrderIndex = stateOrder.get(String(stateName).toLowerCase())
    if (stateOrderIndex !== undefined) {
      return stateOrderIndex
    }

    const numericSuffix = String(stateName).match(/(\d+)(?!.*\d)/)
    if (numericSuffix) {
      return Number(numericSuffix[1])
    }
  }

  return Number.MAX_SAFE_INTEGER
}

const sortHeadlineDataByAttitudeState = (dataArray: any[], stateOrder: Map<string, number>) => {
  return dataArray
    .map((record, originalIndex) => ({ record, originalIndex }))
    .sort((a, b) => {
      const projectCompare = String(a.record.project || '').localeCompare(
        String(b.record.project || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      )
      if (projectCompare !== 0) {
        return projectCompare
      }

      const runCompare = String(a.record.run || '').localeCompare(
        String(b.record.run || ''),
        undefined,
        {
          numeric: true,
          sensitivity: 'base'
        }
      )
      if (runCompare !== 0) {
        return runCompare
      }

      // Default to alphabetical state order so states align across consecutive runs
      const stateNameCompare = String(a.record.state_name || '').localeCompare(
        String(b.record.state_name || ''),
        undefined,
        { numeric: true, sensitivity: 'base' }
      )
      if (stateNameCompare !== 0) {
        return stateNameCompare
      }

      const attitudeIndexCompare =
        getAttitudeStateSortIndex(a.record, stateOrder) -
        getAttitudeStateSortIndex(b.record, stateOrder)
      if (attitudeIndexCompare !== 0) {
        return attitudeIndexCompare
      }

      return a.originalIndex - b.originalIndex
    })
    .map(item => item.record)
}

const setTreeNodeExpanded = (node: any, expanded: boolean) => {
  if (!node) {
    return
  }

  node.setExpanded(expanded)
  node.childrenAfterGroup?.forEach((childNode: any) => {
    setTreeNodeExpanded(childNode, expanded)
  })
}

const colsVisibility = [
  { label: 'Case', field: 'gm_id', visible: true, group: 'Identity' },
  { label: 'Ref', field: 'ref_gm_id', visible: true, group: 'Identity' },
  { label: 'Ref 2', field: REF_GM_ID_2ND, visible: true, group: 'Identity' },
  { label: 'AHF', field: 'ahf', visible: true, group: 'Attitude' },
  { label: 'AHR', field: 'ahr', visible: true, group: 'Attitude' },
  { label: 'Yaw', field: 'yaw', visible: true, group: 'Attitude' },
  { label: 'Roll', field: 'roll', visible: true, group: 'Attitude' },
  { label: 'Steer', field: 'steer', visible: true, group: 'Attitude' },
  { label: 'MPH', field: 'speed', visible: true, group: 'Attitude' },
  { label: 'Δ CD', field: 'delta_cd', visible: true, group: 'Deltas' },
  { label: 'Δ CL', field: 'delta_cl', visible: true, group: 'Deltas' },
  { label: 'Δ CLF', field: 'delta_clf', visible: true, group: 'Deltas' },
  { label: 'Δ CLR', field: 'delta_clr', visible: true, group: 'Deltas' },
  { label: 'Δ CS', field: 'delta_cs', visible: true, group: 'Deltas' },
  { label: 'Δ CSF', field: 'delta_csf', visible: true, group: 'Deltas' },
  { label: 'Δ CSR', field: 'delta_csr', visible: true, group: 'Deltas' },
  { label: 'Δ %CLF', field: 'delta_pct_clf', visible: true, group: 'Deltas' },
  { label: 'CD', field: 'cd', visible: true, group: 'Coefficients' },
  { label: 'CL', field: 'cl', visible: true, group: 'Coefficients' },
  { label: 'CLF', field: 'clf', visible: true, group: 'Coefficients' },
  { label: 'CLR', field: 'clr', visible: true, group: 'Coefficients' },
  { label: 'CS', field: 'cs', visible: true, group: 'Coefficients' },
  { label: 'CSF', field: 'csf', visible: true, group: 'Coefficients' },
  { label: 'CSR', field: 'csr', visible: true, group: 'Coefficients' },
  { label: '%CLF', field: 'pct_clf', visible: true, group: 'Coefficients' },
  { label: 'Rad VR', field: 'vr', visible: true, group: 'Radiator' },
  { label: 'Δ Rad VR', field: 'delta_vr', visible: true, group: 'Radiator' },
  {
    label: 'LF Heat Transfer (W)',
    field: 'LF_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Tire Heat'
  },
  {
    label: 'RF Heat Transfer (W)',
    field: 'RF_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Tire Heat'
  },
  {
    label: 'LR Heat Transfer (W)',
    field: 'LR_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Tire Heat'
  },
  {
    label: 'RR Heat Transfer (W)',
    field: 'RR_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Tire Heat'
  },
  {
    label: 'LF Heat Transfer (%Δ)',
    field: 'pct_change_LF_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Heat Transfer % Change'
  },
  {
    label: 'RF Heat Transfer (%Δ)',
    field: 'pct_change_RF_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Heat Transfer % Change'
  },
  {
    label: 'LR Heat Transfer (%Δ)',
    field: 'pct_change_LR_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Heat Transfer % Change'
  },
  {
    label: 'RR Heat Transfer (%Δ)',
    field: 'pct_change_RR_Heat_Transfer_Monitor_W',
    visible: false,
    group: 'Heat Transfer % Change'
  },
  {
    label: 'Rotor Heat Transfer (kW)',
    field: 'FLUID_HEAT_TRANSFER_ROTOR_TOTAL_Monitor_kW',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Caliper Heat Transfer (kW)',
    field: 'FLUID_HEAT_TRANSFER_CALIPER_Monitor_kW',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Pads Heat Transfer (kW)',
    field: 'FLUID_HEAT_TRANSFER_PADS_TOTAL_Monitor_kW',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Pistons Heat Transfer (kW)',
    field: 'FLUID_HEAT_TRANSFER_PISTONS_TOTAL_Monitor_kW',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Net Fluid Heat Transfer (W)',
    field: 'FLUID_HEAT_TRANSFER_INLET_OUTLET_Monitor_W',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Vane Heat Transfer (kW)',
    field: 'FLUID_HEAT_TRANSFER_VANE_Monitor_kW',
    visible: false,
    group: 'Brake Heat'
  },
  {
    label: 'Caliper HTC',
    field: 'Caliper_HTC_Monitor_W_m_2_K',
    visible: false,
    group: 'Heat Transfer Coeff'
  },
  {
    label: 'Rotor HTC',
    field: 'Rotor_Total_Monitor_W_m_2_K',
    visible: false,
    group: 'Heat Transfer Coeff'
  },
  {
    label: 'Duct Flow (g/s)',
    field: 'Duct_INLET_OUTLET_MASS_FLOW_Monitor_g_s',
    visible: false,
    group: 'Mass Flow'
  },
  {
    label: 'Inlet Flow (kg/s)',
    field: 'Mass_Flow_Global_Inlet_Monitor_kg_s',
    visible: false,
    group: 'Mass Flow'
  },
  {
    label: 'Outlet Flow (kg/s)',
    field: 'Mass_Flow_Global_Outlet_Monitor_kg_s',
    visible: false,
    group: 'Mass Flow'
  },
  {
    label: 'Cab Cp',
    field: 'Cab_Pressure_Monitor_Maximum_of_Pressure_Coefficient',
    visible: false,
    group: 'Pressures'
  },
  {
    label: 'Engine Bay P',
    field: 'Engine_Bay_Pressure_Monitor',
    visible: false,
    group: 'Pressures'
  },
  { label: 'Trunk P', field: 'Trunk_Pressure_Monitor', visible: false, group: 'Pressures' },
  { label: 'FW CD', field: 'FW_CD_Monitor', visible: false, group: 'Component Forces' },
  { label: 'FW CL', field: 'FW_CL_Monitor', visible: false, group: 'Component Forces' },
  { label: 'RW CD', field: 'RW_CD_Monitor', visible: false, group: 'Component Forces' },
  { label: 'RW CL', field: 'RW_CL_Monitor', visible: false, group: 'Component Forces' },
  { label: 'Floor CL', field: 'Floor_CL_Monitor', visible: false, group: 'Component Forces' },
  {
    label: 'Underwing CD',
    field: 'CD_underwing_Monitor',
    visible: false,
    group: 'Component Forces'
  },
  {
    label: 'Underwing CL',
    field: 'CL_underwing_Monitor',
    visible: false,
    group: 'Component Forces'
  },
  {
    label: 'Underwing CLf',
    field: 'CLf_underwing_Monitor',
    visible: false,
    group: 'Component Forces'
  },
  {
    label: 'Underwing CLr',
    field: 'CLr_underwing_Monitor',
    visible: false,
    group: 'Component Forces'
  },
  {
    label: 'Domain HT (kW)',
    field: 'Domain_HT_Balance_Monitor_kW',
    visible: false,
    group: 'Thermal'
  },
  {
    label: 'Duct Max T (K)',
    field: 'Duct_MAX_TEMP_MONITOR_Monitor_K',
    visible: false,
    group: 'Thermal'
  },
  { label: 'LS Inlet CpT', field: 'LS_Inlet_CpT_Monitor', visible: false, group: 'Inlet Quality' },
  { label: 'RS Inlet CpT', field: 'RS_Inlet_CpT_Monitor', visible: false, group: 'Inlet Quality' },
  {
    label: 'Convergence Criteria',
    field: 'Convergence_Criteria_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean CD',
    field: 'Primary_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean CLf',
    field: 'Primary_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean CLr',
    field: 'Primary_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean Var Mean CD',
    field: 'Primary_Mean_Variance_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean Var Mean CLf',
    field: 'Primary_Mean_Variance_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Mean Var Mean CLr',
    field: 'Primary_Mean_Variance_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Var Mean CD',
    field: 'Primary_Variance_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Var Mean CLf',
    field: 'Primary_Variance_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Primary Var Mean CLr',
    field: 'Primary_Variance_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean CD',
    field: 'Secondary_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean CLf',
    field: 'Secondary_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean CLr',
    field: 'Secondary_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean Var Mean CD',
    field: 'Secondary_Mean_Variance_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean Var Mean CLf',
    field: 'Secondary_Mean_Variance_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Mean Var Mean CLr',
    field: 'Secondary_Mean_Variance_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Var Mean CD',
    field: 'Secondary_Variance_Mean_CD_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Var Mean CLf',
    field: 'Secondary_Variance_Mean_CLf_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Secondary Var Mean CLr',
    field: 'Secondary_Variance_Mean_CLr_Monitor',
    visible: false,
    group: 'Convergence'
  },
  {
    label: 'Solver CPU Time (hr)',
    field: 'Total_Solver_CPU_Time_Monitor_hr',
    visible: false,
    group: 'Solver Time'
  },
  {
    label: 'Solver Elapsed Time (hr)',
    field: 'Total_Solver_Elapsed_Time_Monitor_hr',
    visible: false,
    group: 'Solver Time'
  },
  {
    label: 'AMG Cycles',
    field: 'AMG_Cycles_Monitor_AMG_Cycles',
    visible: false,
    group: 'Residuals'
  },
  {
    label: 'CFL Number',
    field: 'CFL_Number_Monitor_CFL_Number',
    visible: false,
    group: 'Residuals'
  },
  { label: 'Continuity', field: 'Continuity_Continuity', visible: false, group: 'Residuals' },
  { label: 'PerF', field: 'PerF_Monitor_Expression', visible: false, group: 'Residuals' },
  { label: 'Sdr', field: 'Sdr_Sdr', visible: false, group: 'Residuals' },
  { label: 'Tke', field: 'Tke_Tke', visible: false, group: 'Residuals' },
  {
    label: 'VMax (mph)',
    field: 'VMax_Monitor_Maximum_of_Velocity_Magnitude_mph',
    visible: false,
    group: 'Residuals'
  },
  { label: 'X-momentum', field: 'X_momentum_X_momentum', visible: false, group: 'Residuals' },
  { label: 'Y-momentum', field: 'Y_momentum_Y_momentum', visible: false, group: 'Residuals' },
  { label: 'Z-momentum', field: 'Z_momentum_Z_momentum', visible: false, group: 'Residuals' },
  { label: 'End Time', field: 'end_time', visible: true, group: 'Other' }
]

// Backfilled by the CFD pipeline and may be absent; their toggles are hidden until the
// column actually loads so the Show/Hide panel never shows a dead switch.
const OPTIONAL_HEADLINE_FIELDS = new Set([
  'LF_Heat_Transfer_Monitor_W',
  'RF_Heat_Transfer_Monitor_W',
  'LR_Heat_Transfer_Monitor_W',
  'RR_Heat_Transfer_Monitor_W',
  'FLUID_HEAT_TRANSFER_ROTOR_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_CALIPER_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PADS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PISTONS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_INLET_OUTLET_Monitor_W',
  'FLUID_HEAT_TRANSFER_VANE_Monitor_kW',
  'Caliper_HTC_Monitor_W_m_2_K',
  'Rotor_Total_Monitor_W_m_2_K',
  'Duct_INLET_OUTLET_MASS_FLOW_Monitor_g_s',
  'Mass_Flow_Global_Inlet_Monitor_kg_s',
  'Mass_Flow_Global_Outlet_Monitor_kg_s',
  'Cab_Pressure_Monitor_Maximum_of_Pressure_Coefficient',
  'Engine_Bay_Pressure_Monitor',
  'Trunk_Pressure_Monitor',
  'FW_CD_Monitor',
  'FW_CL_Monitor',
  'RW_CD_Monitor',
  'RW_CL_Monitor',
  'Floor_CL_Monitor',
  'Domain_HT_Balance_Monitor_kW',
  'Duct_MAX_TEMP_MONITOR_Monitor_K',
  'LS_Inlet_CpT_Monitor',
  'RS_Inlet_CpT_Monitor',
  'pct_change_LF_Heat_Transfer_Monitor_W',
  'pct_change_RF_Heat_Transfer_Monitor_W',
  'pct_change_LR_Heat_Transfer_Monitor_W',
  'pct_change_RR_Heat_Transfer_Monitor_W',
  'vr',
  'delta_vr',
  'Convergence_Criteria_Monitor',
  'Primary_Mean_CD_Monitor',
  'Primary_Mean_CLf_Monitor',
  'Primary_Mean_CLr_Monitor',
  'Primary_Mean_Variance_Mean_CD_Monitor',
  'Primary_Mean_Variance_Mean_CLf_Monitor',
  'Primary_Mean_Variance_Mean_CLr_Monitor',
  'Primary_Variance_Mean_CD_Monitor',
  'Primary_Variance_Mean_CLf_Monitor',
  'Primary_Variance_Mean_CLr_Monitor',
  'Secondary_Mean_CD_Monitor',
  'Secondary_Mean_CLf_Monitor',
  'Secondary_Mean_CLr_Monitor',
  'Secondary_Mean_Variance_Mean_CD_Monitor',
  'Secondary_Mean_Variance_Mean_CLf_Monitor',
  'Secondary_Mean_Variance_Mean_CLr_Monitor',
  'Secondary_Variance_Mean_CD_Monitor',
  'Secondary_Variance_Mean_CLf_Monitor',
  'Secondary_Variance_Mean_CLr_Monitor',
  'Total_Solver_CPU_Time_Monitor_hr',
  'Total_Solver_Elapsed_Time_Monitor_hr',
  'AMG_Cycles_Monitor_AMG_Cycles',
  'CFL_Number_Monitor_CFL_Number',
  'Continuity_Continuity',
  'PerF_Monitor_Expression',
  'Sdr_Sdr',
  'Tke_Tke',
  'VMax_Monitor_Maximum_of_Velocity_Magnitude_mph',
  'X_momentum_X_momentum',
  'Y_momentum_Y_momentum',
  'Z_momentum_Z_momentum',
  'CD_underwing_Monitor',
  'CL_underwing_Monitor',
  'CLf_underwing_Monitor',
  'CLr_underwing_Monitor'
])

const RUN_LEVEL_AVERAGE_FIELDS = [
  'cd',
  'cl',
  'clf',
  'clr',
  'csf',
  'cs',
  'csf',
  'csr',
  'pct_clf',
  'delta_cd',
  'delta_cl',
  'delta_clf',
  'delta_clr',
  'delta_cs',
  'delta_csf',
  'delta_csr',
  'delta_pct_clf',
  'vr',
  'delta_vr',
  'LF_Heat_Transfer_Monitor_W',
  'RF_Heat_Transfer_Monitor_W',
  'LR_Heat_Transfer_Monitor_W',
  'RR_Heat_Transfer_Monitor_W',
  'FLUID_HEAT_TRANSFER_ROTOR_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_CALIPER_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PADS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PISTONS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_INLET_OUTLET_Monitor_W',
  'FLUID_HEAT_TRANSFER_VANE_Monitor_kW',
  'Caliper_HTC_Monitor_W_m_2_K',
  'Rotor_Total_Monitor_W_m_2_K',
  'Domain_HT_Balance_Monitor_kW',
  'Duct_MAX_TEMP_MONITOR_Monitor_K',
  'pct_change_LF_Heat_Transfer_Monitor_W',
  'pct_change_RF_Heat_Transfer_Monitor_W',
  'pct_change_LR_Heat_Transfer_Monitor_W',
  'pct_change_RR_Heat_Transfer_Monitor_W'
] as const

// Heat-rejection fields that render a run-level average using each field's own
// formatter (not the coefficient cellDataFormatter, which forces 3 decimals).
const HEAT_RUN_AVERAGE_FIELDS = new Set<string>([
  'LF_Heat_Transfer_Monitor_W',
  'RF_Heat_Transfer_Monitor_W',
  'LR_Heat_Transfer_Monitor_W',
  'RR_Heat_Transfer_Monitor_W',
  'FLUID_HEAT_TRANSFER_ROTOR_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_CALIPER_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PADS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_PISTONS_TOTAL_Monitor_kW',
  'FLUID_HEAT_TRANSFER_INLET_OUTLET_Monitor_W',
  'FLUID_HEAT_TRANSFER_VANE_Monitor_kW',
  'Caliper_HTC_Monitor_W_m_2_K',
  'Rotor_Total_Monitor_W_m_2_K',
  'Domain_HT_Balance_Monitor_kW',
  'Duct_MAX_TEMP_MONITOR_Monitor_K',
  'pct_change_LF_Heat_Transfer_Monitor_W',
  'pct_change_RF_Heat_Transfer_Monitor_W',
  'pct_change_LR_Heat_Transfer_Monitor_W',
  'pct_change_RR_Heat_Transfer_Monitor_W'
])

const createDataTree = (
  dataArray: any[],
  stateOrder: Map<string, number>,
  coefficientData: Record<string, any>
) => {
  console.log('createSimpleRunTree')
  // Define which metrics to average
  type RawTreeNode = {
    key: string
    data: Record<string, any>
    children?: Map<string, RawTreeNode> | any[]
  }

  const dataMap = new Map<string, any>()
  const runDataMap = new Map<string, any[]>()
  const treeData = []
  sortHeadlineDataByAttitudeState(dataArray, stateOrder).forEach(record => {
    const { project, run, gm_id, state_name, project_desc, run_desc } = record

    if (!project || !run || !gm_id) return

    // Project level
    const projKey = project
    if (!dataMap.has(projKey)) {
      dataMap.set(projKey, {} as RawTreeNode)
      treeData.push({
        id: projKey,
        name: projKey + (project_desc ? ' (' + project_desc + ')' : ''),
        treeLevel: TREE_LEVEL_PROJECT
      })
    }

    // run level
    const runKey = `${project}${KEY_DELIMITER}${run}`
    if (!dataMap.has(runKey)) {
      dataMap.set(runKey, {} as RawTreeNode)
      const runCoefficient = coefficientData[run]
      const runNode = {
        id: runKey,
        name: run + (run_desc ? ' (' + run_desc + ')' : ''),
        parentId: projKey,
        treeLevel: TREE_LEVEL_RUN
      }
      if (runCoefficient) {
        runNode['cdrun'] = runCoefficient.cdrun
        runNode['clrun'] = runCoefficient.clrun
        runNode['csrun'] = runCoefficient.csrun
        runNode['clfrun'] = runCoefficient.clfrun
        runNode['clrrun'] = runCoefficient.clrrun
        runNode['pct_clfrun'] = runCoefficient.pct_frontrun
      }

      treeData.push(runNode)
      runDataMap.set(runKey, [])
    }

    // case/state level
    const caseKey = `${project}${KEY_DELIMITER}${run}${KEY_DELIMITER}${state_name}`
    if (!dataMap.has(caseKey)) {
      dataMap.set(caseKey, {} as RawTreeNode)
      const caseNode = {
        id: caseKey,
        name: state_name,
        parentId: runKey,
        treeLevel: TREE_LEVEL_CASE,
        ...record
      }
      treeData.push(caseNode)
      // Attach case data to the run node for easy access when calculating averages at run level
      runDataMap.get(runKey)?.push({ ...record })
    }
  })

  runDataMap.forEach((cases, runKey) => {
    // Calculate averages for the run
    const runAverages: Record<string, any> = {}
    RUN_LEVEL_AVERAGE_FIELDS.forEach(metric => {
      let hasValue = false
      const sum = cases.reduce((acc, curr) => {
        const raw = curr[metric]
        const value = Number(raw)
        if (raw !== null && raw !== undefined && raw !== '' && Number.isFinite(value)) {
          hasValue = true
          return acc + value
        }
        return acc
      }, 0)
      const count = cases.length
      runAverages[metric] = count > 0 && hasValue ? sum / count : null
    })

    const theRunNode = treeData.find(node => node.id === runKey)
    // Attach cases to the run node for easy access when filtering at run level
    if (theRunNode) {
      Object.assign(theRunNode, runAverages)
      theRunNode.cases = [...cases]
      // Run completion date = latest completion among its cases (drives display and sorting).
      let latestEndTime: any = null
      let latestEndMs = -Infinity
      cases.forEach(currentCase => {
        const raw = currentCase.end_time
        if (raw === null || raw === undefined || raw === '') {
          return
        }
        const ms = new Date(raw).getTime()
        if (Number.isFinite(ms) && ms > latestEndMs) {
          latestEndMs = ms
          latestEndTime = raw
        }
      })
      theRunNode.end_time = latestEndTime
      // console.log('cases for run', runKey, cases, runAverages)
    }
  })

  // console.log('Final tree data:', treeData)

  const projects = treeData.filter(node => node.treeLevel === TREE_LEVEL_PROJECT)
  projects.forEach(projNode => {
    // Get all runs under this project
    const cases = treeData.filter(caseNode => {
      // console.log('Checking case node:', caseNode, 'against project node:', projNode)
      return (
        caseNode.treeLevel === TREE_LEVEL_CASE &&
        caseNode.parentId.startsWith(projNode.id + KEY_DELIMITER)
      )
    })
    Object.assign(projNode, {
      stateChildren: cases.map(c => c.id).join(',')
    })
  })

  return treeData
}

type NewPlotData = {
  projectRunSelection: Record<string, boolean>
  states: string[]
  baselines: string[]
}

const HeadlineData: FC<{
  onNewPlot: (data: NewPlotData) => void
  onGenerateReport?: (run: ReportRunSelection) => void
  onGoToMedia?: (run: ReportRunSelection) => void
}> = ({ onNewPlot, onGenerateReport, onGoToMedia }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const urlCaseId = (searchParams.get('caseId') || searchParams.get('case') || '').trim()
  const urlProject = (searchParams.get('project') || '').trim()
  const urlRunId = (searchParams.get('runId') || searchParams.get('run') || '').trim()
  const urlPreloadKey = urlCaseId ? `caseId:${urlCaseId}` : urlRunId ? `runId:${urlRunId}` : ''
  const urlProjectRunSelection = useMemo(() => {
    if (urlCaseId || !urlProject || !urlRunId) {
      return null
    }

    return {
      [urlProject]: { partialChecked: true },
      [`${urlProject}::${urlRunId}`]: { checked: true }
    }
  }, [urlCaseId, urlProject, urlRunId])
  const gridRef = useRef<AgGridReact | null>(null)
  const gridContainerRef = useRef<HTMLDivElement | null>(null)
  const [gridSearchText, setGridSearchText] = useState('')
  const [completionSortOrder, setCompletionSortOrder] = useState<'asc' | 'desc'>('desc')

  // Drive the first column's text filter from the search bar instead of a floating filter.
  const handleGridSearch = (value: string) => {
    setGridSearchText(value)
    const api = gridRef.current?.api
    if (!api) {
      return
    }
    api
      .setColumnFilterModel(
        'ag-Grid-AutoColumn',
        value ? { filterType: 'text', type: 'contains', filter: value } : null
      )
      .then(() => api.onFilterChanged())
  }

  // Sort the tree by the completion-date column (runs by their latest case, cases by their own).
  const sortByCompletionDate = (order: 'asc' | 'desc') => {
    setCompletionSortOrder(order)
    gridRef.current?.api?.applyColumnState({
      state: [{ colId: 'end_time', sort: order }],
      defaultState: { sort: null }
    })
  }

  const completionSortItems = [
    {
      label: 'Completion Date',
      icon: 'pi pi-arrow-down',
      disabled: completionSortOrder === 'desc',
      command: () => sortByCompletionDate('desc')
    },
    {
      label: 'Completion Date',
      icon: 'pi pi-arrow-up',
      disabled: completionSortOrder === 'asc',
      command: () => sortByCompletionDate('asc')
    }
  ]
  const [{ loading: loadingHeadlineData }, getHeadlineData] = useGetHeadlineData()
  const [, getReferenceRunProjectData] = useGetHeadlineData()
  const [, getHeadlineDraftingInfo] = useGetHeadlineDraftingInfo()
  const [, getHeadlineDataByCase] = useGetHeadlineDataByCase()
  const [{ loading: loadingRefRun }, getRunCase] = useGetRunCase()
  const [{ loading: loadingCupProjectRunCoefficientData }, getCupProjectRunCoefficientData] =
    useGetCupProjectRunCoefficientData()
  const [{ loading: coefficientRecalculating }, recalculateCupRunCoefficientData] =
    useRecalculateCupRunCoefficientData()
  const [{ loading: coefficientRecalculateStatusLoading }, getCupRunCoefficientRecalculateStatus] =
    useGetCupRunCoefficientRecalculateStatus()
  const [columns, setColumns] = useState([])
  const [exportingGridImage, setExportingGridImage] = useState(false)
  const getRowId = useCallback(params => String(params.data.id), [])
  const currentTeam = useRecoilValue(currentTeamState)
  const currentUser = useRecoilValue(currentUserState)
  const isDashboardOnly = isDashboardOnlyRole(currentUser?.role?.roleId)
  const userCanMutate = canMutate(currentUser?.role?.roleId)
  const attitudeSets = useRecoilValue(attitudeSetListState)
  const appTheme = useRecoilValue(themeState)
  const gridTheme = useMemo(() => getAgGridTheme(appTheme), [appTheme])
  const attitudeStateOrder = useMemo(() => buildAttitudeStateOrderMap(attitudeSets), [attitudeSets])
  const [showFilter, setShowFilter] = useState(false)
  const [panelCollapsed, setPanelCollapsed] = useState(false)
  const refXAxis = useRef<string | null>(null)
  const refYAxis = useRef<string | null>(null)
  const deltaAvgExclusionsRef = useRef<DeltaAvgExclusions>(
    readDeltaAvgExclusionsFromStorage(currentTeam)
  )
  // State for selected cases (up to 2 for side-by-side gallery comparison)
  const [selectedCases, setSelectedCases] = useState<
    Array<{
      systemRunId: string
      runId: string
      caseName: string
      caseId: string
      baselineRunId?: string
      ref2RunId?: string
    }>
  >([])

  const [syncedIndex, setSyncedIndex] = useState<number | null>(null)
  const [galleriesLinked, setGalleriesLinked] = useState(true)
  const [showGifModal, setShowGifModal] = useState(false)
  const [showGifPlayer, setShowGifPlayer] = useState(false)
  const refCupProjectRunCoefficientData = useRef({})
  const galleryPanelRef = useRef<HTMLDivElement | null>(null)

  // The gallery docks below a full-height grid, so it would otherwise open off-screen.
  // Pull it into view whenever the set of displayed cases changes.
  const galleryOpen = selectedCases.length > 0
  const galleryCaseKey = selectedCases.map(c => c.caseId).join('|')
  useEffect(() => {
    if (!galleryCaseKey) return
    const frame = requestAnimationFrame(() => {
      galleryPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
    })
    return () => cancelAnimationFrame(frame)
  }, [galleryCaseKey])

  // Fullscreen comparison state
  const [fullscreenGalleryIndex, setFullscreenGalleryIndex] = useState<number | null>(null)
  const [galleryImages, setGalleryImages] = useState<
    Array<{ currentImage: any | null; allImages: any[] }>
  >([
    { currentImage: null, allImages: [] },
    { currentImage: null, allImages: [] }
  ])

  const [isLoadingFilter, setIsLoadingFilter] = useState<boolean>(false)
  const [isLoading2ndRef, setIsLoading2ndRef] = useState<boolean>(false)
  const [areAllProjectsExpanded, setAreAllProjectsExpanded] = useState(true)

  const syncProjectExpansionState = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) {
      return
    }

    let hasProjectNodes = false
    let allExpanded = true

    api.forEachNode(node => {
      if (node.data?.treeLevel !== TREE_LEVEL_PROJECT) {
        return
      }

      hasProjectNodes = true
      if (!node.expanded) {
        allExpanded = false
      }
    })

    setAreAllProjectsExpanded(hasProjectNodes ? allExpanded : true)
  }, [])

  const sizeHeadlineGridColumns = useCallback(() => {
    const api = gridRef.current?.api
    if (!api) {
      return
    }

    window.requestAnimationFrame(() => {
      // Fit the group column to its own content first; sizeColumnsToFit then shares out the rest.
      api.autoSizeColumns(['ag-Grid-AutoColumn'], false)
      api.sizeColumnsToFit()
    })
  }, [])

  const recalculateCoefficient = useCallback(
    async (project: string, run: string, state: string, action: '+' | '-') => {
      try {
        const submitRes = await recalculateCupRunCoefficientData({
          run,
          exclusion: action + state
        } as any)
        const runId = submitRes?.data?.run_id ?? submitRes?.data?.runId

        if (!runId) {
          throw new Error('Missing Databricks run id in recalculation response.')
        }

        const maxAttempts = 100 // 5 minutes at 3s intervals
        const pollIntervalMs = 3000

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
          // check the status of the recalculation job
          const statusRes = await getCupRunCoefficientRecalculateStatus({ runId } as any)
          const statusData = statusRes?.data || {}
          const lifeCycleState = statusData?.state?.life_cycle_state
          const resultState = statusData?.state?.result_state

          if (lifeCycleState === 'TERMINATED') {
            if (resultState && resultState !== 'SUCCESS') {
              toast.error(
                `Coefficient recalculation ended with status ${resultState}. Please review Databricks run details.`
              )
            } else {
              toast.success('Coefficient recalculation completed.')
            }

            // retrieve the updated coefficient data for the run
            const coefficientRes = await getCupProjectRunCoefficientData({ project, run } as any)

            if (coefficientRes?.data) {
              coefficientRes.data.forEach((item: any) => {
                if (item?.run) {
                  refCupProjectRunCoefficientData.current[item.run] = item
                  // here we need to update the treeData with the new coefficient data for the run
                  const api = gridRef.current?.api
                  if (api) {
                    const runNode = api.getRowNode(`${project}${KEY_DELIMITER}${run}`)
                    if (runNode) {
                      runNode.setDataValue('cdrun', item.cdrun)
                      runNode.setDataValue('clrun', item.clrun)
                      runNode.setDataValue('csrun', item.csrun)
                      runNode.setDataValue('clfrun', item.clfrun)
                      runNode.setDataValue('clrrun', item.clrrun)
                      runNode.setDataValue('pct_clfrun', item.pct_frontrun)
                    }
                  }
                }
              })
              // The excluded-state strikethrough reads excluded_states from the coefficient ref
              // inside the group-cell renderer, so force a group-column refresh to restyle the
              // affected case rows without requiring a page reload.
              gridRef.current?.api?.refreshCells({
                force: true,
                columns: ['ag-Grid-AutoColumn']
              })
            }

            return
          }

          if (lifeCycleState === 'INTERNAL_ERROR' || lifeCycleState === 'SKIPPED') {
            toast.error(
              `Coefficient recalculation ended in ${lifeCycleState}. Please review Databricks run details.`
            )
            return
          }

          await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
        }

        toast.error(
          'Coefficient recalculation is still running. Please check status again shortly.'
        )
      } catch (err) {
        console.error('Error submitting or checking recalculation job:', err)
        toast.error('Failed to submit or monitor recalculation job. Please try again.')
      }
    },
    [
      getCupProjectRunCoefficientData,
      getCupRunCoefficientRecalculateStatus,
      recalculateCupRunCoefficientData
    ]
  )

  const toggleAllProjectNodes = useCallback(
    (recursive: boolean = false) => {
      const api = gridRef.current?.api
      if (!api) {
        return
      }

      const nextExpanded = !areAllProjectsExpanded

      let hasProjectNodes = false

      api.forEachNode(node => {
        if (node.data?.treeLevel === TREE_LEVEL_PROJECT) {
          hasProjectNodes = true
          if (recursive) {
            setTreeNodeExpanded(node, nextExpanded)
          } else {
            node.setExpanded(nextExpanded)
            // When expanding without shift, collapse all descendants so only
            // the project level is visible.
            if (nextExpanded) {
              node.childrenAfterGroup?.forEach((childNode: any) => {
                setTreeNodeExpanded(childNode, false)
              })
            }
          }
        }
      })

      setAreAllProjectsExpanded(hasProjectNodes ? nextExpanded : true)
    },
    [areAllProjectsExpanded]
  )

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Project / Run / State',
      field: 'name',
      width: 200,
      minWidth: 200,
      maxWidth: 360,
      flex: 0,
      // Text filter on the first column; driven by the search bar above the grid (no floating filter).
      // Matches on run id, name and description.
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        maxNumConditions: 1,
        // Space-separated tokens act as multiple AND filters: a row matches only when every token is found.
        textMatcher: ({ value, filterText }: any) => {
          if (!filterText) {
            return true
          }
          const haystack = String(value ?? '').toLowerCase()
          return filterText
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean)
            .every((term: string) => haystack.includes(term))
        }
      },
      filterValueGetter: (params: any) => {
        const node = params.data || {}
        // Projects aren't filtered directly; they only appear as ancestors of matching rows.
        if (node.treeLevel === TREE_LEVEL_PROJECT) {
          return ''
        }
        // Run nodes carry the run only inside their id (project|run); case nodes have `run`.
        const runFromId = String(node.id || '').split(KEY_DELIMITER)[1] || ''
        // Case nodes store run_desc without parentheses, so also surface the full displayed run
        // label ("R100 (baseline)") so searching that label still matches the cases and keeps them
        // visible under their run (excludeChildrenWhenTreeDataFiltering filters each node alone).
        const runLabel =
          node.treeLevel === TREE_LEVEL_RUN
            ? node.name
            : `${node.run || runFromId}${node.run_desc ? ` (${node.run_desc})` : ''}`
        return [node.name, node.run, runFromId, node.run_desc, node.description, runLabel]
          .filter(Boolean)
          .join(' ')
      },
      // Auto-sized to fit its content (incl. drafting badges), so keep sizeColumnsToFit out of it.
      suppressSizeToFit: true,
      headerComponent: (params: any) => {
        return (
          <div className="flex h-full items-center gap-2 pr-2">
            <button
              type="button"
              title="shift + click to expand/collapse all project nodes recursively"
              className="flex h-5 w-5 items-center justify-center rounded text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
              aria-label={`${areAllProjectsExpanded ? 'Collapse' : 'Expand'} all project nodes`}
              onClick={event => {
                event.preventDefault()
                event.stopPropagation()
                toggleAllProjectNodes(event.shiftKey)
              }}>
              <i className={`pi ${areAllProjectsExpanded ? 'pi-minus' : 'pi-plus'} text-xs`} />
            </button>
            <span className="truncate">{params.displayName}</span>
          </div>
        )
      },
      colSpan: (params: any) => {
        const level = params.data?.treeLevel
        if (level !== TREE_LEVEL_RUN && level !== TREE_LEVEL_PROJECT) {
          return 1
        }

        const displayedColumns = params.api.getAllDisplayedColumns()
        const groupColumnId = params.column.getColId()
        const groupColumnIndex = displayedColumns.findIndex(
          column => column.getColId() === groupColumnId
        )
        if (groupColumnIndex < 0) {
          return 1
        }

        // Run rows keep Ref 2 as its own editable cell: stop the group span just before it.
        if (level === TREE_LEVEL_RUN) {
          const ref2ColumnIndex = displayedColumns.findIndex(
            column => column.getColDef().field === REF_GM_ID_2ND
          )
          if (ref2ColumnIndex > groupColumnIndex) {
            return ref2ColumnIndex - groupColumnIndex
          }
        }

        // Span the leading non-metric columns until the first displayed column that
        // holds a Run/Project average value. Run rows also show their completion date, so
        // treat a visible run-level end_time as a boundary too — otherwise, when the metrics
        // are hidden, the group cell would span over and hide the completion date. If no such
        // column is displayed, span to the end so no leading columns are left visible but empty.
        const firstMetricColumnIndex = displayedColumns.findIndex((column, index) => {
          if (index <= groupColumnIndex) {
            return false
          }
          const field = column.getColDef().field
          if (!field) {
            return false
          }
          if (level === TREE_LEVEL_RUN && field === 'end_time') {
            return true
          }
          return (RUN_LEVEL_AVERAGE_FIELDS as readonly string[]).includes(field)
        })

        if (firstMetricColumnIndex > groupColumnIndex) {
          return firstMetricColumnIndex - groupColumnIndex
        }

        return displayedColumns.length - groupColumnIndex
      },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (params: any) => {
          const level = params.data?.treeLevel
          const paddingLeft = level === TREE_LEVEL_PROJECT ? 4 : level === TREE_LEVEL_CASE ? 6 : 0
          const isProjectRow = level === TREE_LEVEL_PROJECT
          let textClass = ''
          if (level === TREE_LEVEL_CASE) {
            // Apply strikethrough style to excluded states at the case level
            const coefficientData = refCupProjectRunCoefficientData?.current[params.data?.run]
            const excludedStates = coefficientData?.excluded_states || ''
            if (excludedStates.includes(params.value)) {
              textClass = 'line-through'
            }
            // Strike through the state/case name when the whole case is excluded from Δ AVG
            const gmId = String(params.data?.gm_id ?? '')
            if (isRowDeltaAvgExcluded(deltaAvgExclusionsRef.current, gmId)) {
              textClass = 'line-through text-neutral-400 dark:text-neutral-600'
            }
          }

          const isExpanded = Boolean(params.node?.expanded)
          const draftRole = level === TREE_LEVEL_CASE ? params.data?.[DRAFT_ROLE_FIELD] : undefined
          return (
            <div className="flex w-full items-center gap-2" style={{ paddingLeft }}>
              {isProjectRow && (
                <button
                  type="button"
                  title="shift + click to expand/collapse all runs recursively"
                  className="flex h-5 w-5 items-center justify-center rounded text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} project ${params.value}`}
                  onClick={event => {
                    event.preventDefault()
                    event.stopPropagation()
                    if (event.shiftKey) {
                      setTreeNodeExpanded(params.node, !isExpanded)
                      return
                    }
                    params.node?.setExpanded(!isExpanded)
                  }}>
                  <i className={`pi ${isExpanded ? 'pi-minus' : 'pi-plus'} text-xs`} />
                </button>
              )}
              <span className={`min-w-0 truncate ${textClass}`}>{params.value}</span>
              {draftRole && (
                <span
                  title={`Drafting pair ${params.data?.[DRAFT_PAIR_INDEX_FIELD]} — ${draftRole} car`}
                  className={`ml-auto shrink-0 rounded-full px-2 py-[1px] text-[10px] font-semibold leading-tight ${getDraftPairBadgeClass(
                    params.data?.[DRAFT_PAIR_INDEX_FIELD]
                  )}`}>
                  {draftRole}
                </span>
              )}
            </div>
          )
        }
      }
    }
  }, [areAllProjectsExpanded, toggleAllProjectNodes])

  const [defaultColDef] = useState({
    flex: 1,
    suppressHeaderMenuButton: true,
    minWidth: 60,
    resizable: true,
    cellStyle: { textAlign: 'right' }
    // headerClass intentionally omitted: it would also apply to the auto-group column,
    // which must stay left-aligned. Right-aligned columns set RIGHT_ALIGNED_HEADER_CLASS individually.
  })

  const [rawHeadlineData, setRawHeadlineData] = useState([])
  const [dataNodes, setDataNodes] = useState<TreeDataNode[]>([])
  const ref2OverridesRef = useRef<Ref2Overrides>(readRef2OverridesFromStorage(currentTeam))
  const ref2CaseOverridesRef = useRef<Ref2Overrides>(
    readRef2OverridesFromStorage(currentTeam, REF2_CASE_STORAGE_KEY_PREFIX)
  )
  const referenceRunCacheRef = useRef(new Map<string, ReferenceRun>())
  const dataNodesRef = useRef<TreeDataNode[]>(dataNodes)
  dataNodesRef.current = dataNodes
  const refProjectRunSelection = useRef<Record<string, boolean>>({})
  const refSelectedProjects = useRef<string[]>([])
  const refSelectedStates = useRef<string[]>([])
  const refSelectedRuns = useRef<string[]>([])
  const refSelectedBaselines = useRef<string[]>([])
  const highlightedUrlPreloadRef = useRef<string>('')

  const recalculateRunLevelAverages = useCallback((nodes: TreeDataNode[]) => {
    if (!nodes || nodes.length === 0) {
      return nodes
    }

    const selectedStates = new Set((refSelectedStates.current || []).map(item => String(item)))
    const selectedBaselines = new Set(
      (refSelectedBaselines.current || []).map(item => String(item))
    )
    const hasStateFilter = selectedStates.size > 0
    const hasBaselineFilter = selectedBaselines.size > 0

    const caseNodesByRun = new Map<string, TreeDataNode[]>()
    nodes.forEach(node => {
      if (node.treeLevel !== TREE_LEVEL_CASE || !node.parentId) {
        return
      }

      const runId = String(node.parentId)
      const runCases = caseNodesByRun.get(runId)
      if (runCases) {
        runCases.push(node)
      } else {
        caseNodesByRun.set(runId, [node])
      }
    })

    return nodes.map(node => {
      if (node.treeLevel !== TREE_LEVEL_RUN) {
        return node
      }

      const runCases = caseNodesByRun.get(String(node.id)) || []
      if (runCases.length === 0) {
        return node
      }

      const filteredCases = runCases.filter(caseNode => {
        const idParts = String(caseNode.id || '').split(KEY_DELIMITER)
        const caseId = String(idParts[2] ?? caseNode.state_name ?? caseNode.name ?? '')
        const baselineValue = caseNode.baseline_run

        if (
          hasBaselineFilter &&
          (baselineValue === null ||
            baselineValue === undefined ||
            !selectedBaselines.has(String(baselineValue)))
        ) {
          return false
        }

        if (hasStateFilter && !selectedStates.has(caseId)) {
          return false
        }

        return true
      })

      const sourceCases = hasStateFilter || hasBaselineFilter ? filteredCases : runCases
      const nextNode = { ...node }
      const exclusions = deltaAvgExclusionsRef.current

      RUN_LEVEL_AVERAGE_FIELDS.forEach(metric => {
        if (sourceCases.length === 0) {
          nextNode[metric] = null
          return
        }

        // Skip cases excluded from Δ AVG entirely, and cases with this specific cell excluded.
        // The denominator counts only included cases so exclusions actually shift the average.
        let sum = 0
        let count = 0
        let hasValue = false
        sourceCases.forEach(currentCase => {
          const gmId = String(currentCase.gm_id ?? '')
          if (isRowDeltaAvgExcluded(exclusions, gmId)) {
            return
          }
          if (isCellDeltaAvgExcluded(exclusions, gmId, metric)) {
            return
          }
          count += 1
          const raw = currentCase[metric]
          const value = Number(raw)
          if (raw !== null && raw !== undefined && raw !== '' && Number.isFinite(value)) {
            sum += value
            hasValue = true
          }
        })

        nextNode[metric] = count > 0 && hasValue ? sum / count : null
      })

      return nextNode
    })
  }, [])

  // Persist the current Δ AVG exclusions and refresh both the run averages and the
  // strikethrough styling on case cells (case nodes keep the same reference, so a forced
  // cell refresh is required for the style change to show).
  const applyDeltaAvgExclusionsUpdate = useCallback(() => {
    writeDeltaAvgExclusionsToStorage(deltaAvgExclusionsRef.current, currentTeam)
    setDataNodes(prevNodes => recalculateRunLevelAverages(prevNodes))
    gridRef.current?.api?.refreshCells({ force: true })
  }, [currentTeam, recalculateRunLevelAverages])

  const toggleRowDeltaAvgExclusion = useCallback(
    (rowData: Record<string, any>) => {
      const gmId = String(rowData?.gm_id ?? '')
      if (!gmId) {
        return
      }
      const exclusions = deltaAvgExclusionsRef.current
      if (exclusions.rows.has(gmId)) {
        exclusions.rows.delete(gmId)
      } else {
        exclusions.rows.add(gmId)
        // Row-level exclusion supersedes any per-cell exclusions for this case.
        exclusions.cells.delete(gmId)
      }
      applyDeltaAvgExclusionsUpdate()
    },
    [applyDeltaAvgExclusionsUpdate]
  )

  const toggleCellDeltaAvgExclusion = useCallback(
    (rowData: Record<string, any>, field: string) => {
      const gmId = String(rowData?.gm_id ?? '')
      if (!gmId || !field) {
        return
      }
      const exclusions = deltaAvgExclusionsRef.current
      const fields = exclusions.cells.get(gmId)
      if (fields?.has(field)) {
        fields.delete(field)
        if (fields.size === 0) {
          exclusions.cells.delete(gmId)
        }
      } else if (fields) {
        fields.add(field)
      } else {
        exclusions.cells.set(gmId, new Set([field]))
      }
      applyDeltaAvgExclusionsUpdate()
    },
    [applyDeltaAvgExclusionsUpdate]
  )

  const [columnsVisibility, setColumnsVisibility] = useState(() => {
    const storedVisibility = localStorage.getItem(HEADLINE_COLUMNS_VISIBILITY)
    if (!storedVisibility) {
      return colsVisibility
    }
    try {
      const parsed = JSON.parse(storedVisibility)
      const storedVisibleByField = new Map<string, boolean>(
        parsed.map(col => [col.field, col.visible])
      )
      // Reconcile against the canonical column list so columns added after the user's
      // visibility was persisted (e.g. delta_cs) still get a toggle entry, while
      // preserving the user's saved visible/hidden choices.
      return colsVisibility.map(col =>
        storedVisibleByField.has(col.field)
          ? { ...col, visible: storedVisibleByField.get(col.field) }
          : col
      )
    } catch {
      return colsVisibility
    }
  })
  const columnsVisibilityOverlayRef = useRef<OverlayPanel>(null)

  // Fields that actually came back from Databricks, used to hide optional dead toggles.
  const loadedColumnFields = useMemo(
    () => new Set((columns as any[]).map(col => col.field)),
    [columns]
  )

  // Groups the visible toggles into labeled sections, dropping any group whose columns
  // all failed to load so we never render an empty section header.
  const groupedColumnsVisibility = useMemo(() => {
    const groups: { name: string; cols: typeof columnsVisibility }[] = []
    const indexByName = new Map<string, number>()
    columnsVisibility
      .filter(col => !OPTIONAL_HEADLINE_FIELDS.has(col.field) || loadedColumnFields.has(col.field))
      .forEach(col => {
        const name = col.group ?? ''
        let idx = indexByName.get(name)
        if (idx === undefined) {
          idx = groups.length
          indexByName.set(name, idx)
          groups.push({ name, cols: [] })
        }
        groups[idx].cols.push(col)
      })
    return groups
  }, [columnsVisibility, loadedColumnFields])

  useEffect(() => {
    const api = gridRef.current?.api
    if (!api) {
      return
    }

    api.applyColumnState({
      state: columnsVisibility.map(col => ({
        colId: col.field,
        hide: !col.visible
      })),
      applyOrder: false
    })
    sizeHeadlineGridColumns()
    // `columns` is a dep so saved visibility re-applies once the column defs finish loading.
  }, [columnsVisibility, columns, sizeHeadlineGridColumns])

  useEffect(() => {
    sizeHeadlineGridColumns()
  }, [columns, dataNodes, showFilter, selectedCases.length, sizeHeadlineGridColumns])

  useEffect(() => {
    ref2OverridesRef.current = readRef2OverridesFromStorage(currentTeam)
    ref2CaseOverridesRef.current = readRef2OverridesFromStorage(
      currentTeam,
      REF2_CASE_STORAGE_KEY_PREFIX
    )
    referenceRunCacheRef.current.clear()
  }, [currentTeam])

  useEffect(() => {
    deltaAvgExclusionsRef.current = readDeltaAvgExclusionsFromStorage(currentTeam)
    setDataNodes(prevNodes => recalculateRunLevelAverages(prevNodes))
    gridRef.current?.api?.refreshCells({ force: true })
  }, [currentTeam, recalculateRunLevelAverages])

  useEffect(() => {
    syncProjectExpansionState()
  }, [dataNodes, syncProjectExpansionState])

  useEffect(() => {
    if (!urlPreloadKey || dataNodes.length === 0) {
      return
    }
    const scopedPreloadKey = `${currentTeam || 'unknown-team'}:${urlPreloadKey}`
    if (highlightedUrlPreloadRef.current === scopedPreloadKey) {
      return
    }

    const normalizedUrlCaseId = urlCaseId.replace(/^0-/, '')
    const targetNodeData = urlCaseId
      ? dataNodes.find(
          node =>
            node.treeLevel === TREE_LEVEL_CASE &&
            String(node.gm_id || '').replace(/^0-/, '') === normalizedUrlCaseId
        )
      : dataNodes.find(
          node =>
            node.treeLevel === TREE_LEVEL_RUN &&
            String(node.id || '').split(KEY_DELIMITER)[1] === urlRunId
        )
    if (!targetNodeData) {
      return
    }

    const api = gridRef.current?.api
    if (!api) {
      return
    }

    const projectId = String(
      targetNodeData.treeLevel === TREE_LEVEL_RUN
        ? String(targetNodeData.id || '').split(KEY_DELIMITER)[0]
        : targetNodeData.project || ''
    )
    const runNodeId = String(
      targetNodeData.treeLevel === TREE_LEVEL_RUN
        ? targetNodeData.id
        : targetNodeData.parentId || ''
    )
    const projectNode = api.getRowNode(projectId)
    const runNode = api.getRowNode(runNodeId)
    projectNode?.setExpanded(true)
    runNode?.setExpanded(true)
    api.onFilterChanged()

    window.requestAnimationFrame(() => {
      const rowNode = api.getRowNode(String(targetNodeData.id))
      if (!rowNode) {
        return
      }
      rowNode.setSelected(true)
      ;(api as any).ensureNodeVisible?.(rowNode, 'middle')
      highlightedUrlPreloadRef.current = scopedPreloadKey
    })
  }, [currentTeam, dataNodes, urlCaseId, urlPreloadKey, urlRunId])

  const [dataCount, setDataCount] = useState<number>(0)

  // Databricks omits `result`/`data_array` when a query returns zero rows, and
  // `total_row_count` can come back as a string, so check for an actual row.
  const headlineCaseHasData = (caseData: any): boolean => {
    const firstRow = caseData?.result?.data_array?.[0]
    return Array.isArray(firstRow) && firstRow.length > 0
  }

  const normalizeHeadlineCaseData = (caseData: any) => {
    if (!caseData?.manifest?.schema?.columns || !headlineCaseHasData(caseData)) {
      return null
    }
    const normalizedCase = {}
    caseData.manifest.schema.columns.forEach(col => {
      normalizedCase[col.name] = caseData.result.data_array[0][col.position]
    })

    return normalizedCase
  }

  // Ref 2 has two layers:
  //  - Run row: a reference *run*. Each case under the run is compared against the case with the
  //    same state in the reference run (PAnchorS -> ref run's PAnchorS, etc.).
  //  - Case row: a reference *case* for just that case. It takes precedence over the run's
  //    reference run until cleared.
  const runKeyForRecord = (record: Record<string, any>) =>
    `${record.project}${KEY_DELIMITER}${record.run}`

  const headlineRecordsFromResponse = (data: any): Record<string, any>[] => {
    const cols = data?.manifest?.schema?.columns || []
    const rows = data?.result?.data_array || []
    return rows.map(row => {
      const record: Record<string, any> = {}
      cols.forEach(col => {
        record[col.name] = row[col.position]
      })
      return record
    })
  }

  const loadedCaseRecords = (extraRecords: Record<string, any>[] = []) => [
    ...extraRecords,
    ...dataNodesRef.current.filter(node => node.treeLevel === TREE_LEVEL_CASE)
  ]

  const buildReferenceRun = (
    project: string,
    run: string,
    records: Record<string, any>[]
  ): ReferenceRun | null => {
    const casesByState = new Map<string, Record<string, any>>()
    records.forEach(record => {
      if (
        String(record.project) !== project ||
        String(record.run) !== run ||
        !record.gm_id ||
        !record.state_name
      ) {
        return
      }
      const state = String(record.state_name)
      if (!casesByState.has(state)) {
        casesByState.set(state, record)
      }
    })

    return casesByState.size > 0 ? { key: `${project}${KEY_DELIMITER}${run}`, casesByState } : null
  }

  // Resolves a run-level Ref 2 entry (a run name, or any case number in the reference run) to
  // the reference run's cases keyed by state. Uses already-loaded grid data when possible.
  const resolveReferenceRun = async (
    token: string,
    extraRecords: Record<string, any>[] = []
  ): Promise<ReferenceRun | null> => {
    const trimmed = String(token || '').trim()
    if (!trimmed) {
      return null
    }
    const cacheKey = trimmed.toLowerCase()
    const cached = referenceRunCacheRef.current.get(cacheKey)
    if (cached) {
      return cached
    }

    const localRecords = loadedCaseRecords(extraRecords)
    let project = ''
    let run = ''
    if (/^\d+$/.test(trimmed)) {
      const localCase = localRecords.find(record => String(record.gm_id) === trimmed)
      if (localCase) {
        project = String(localCase.project)
        run = String(localCase.run)
      } else {
        const { data: caseData } = await getHeadlineDataByCase(trimmed)
        const refCase = normalizeHeadlineCaseData(caseData) as Record<string, any> | null
        if (!refCase?.project || !refCase?.run) {
          return null
        }
        project = String(refCase.project)
        run = String(refCase.run)
      }
    } else {
      const localCase = localRecords.find(
        record => String(record.run || '').toLowerCase() === cacheKey
      )
      if (!localCase) {
        return null
      }
      project = String(localCase.project)
      run = String(localCase.run)
    }

    let referenceRun = buildReferenceRun(project, run, localRecords)
    if (!referenceRun) {
      const res = (await getReferenceRunProjectData({
        page: 0,
        pageSize: 10000,
        projects: project
      } as any)) as any
      referenceRun = buildReferenceRun(project, run, headlineRecordsFromResponse(res?.data))
    }

    if (referenceRun) {
      referenceRunCacheRef.current.set(cacheKey, referenceRun)
    }
    return referenceRun
  }

  const resolveReferenceCase = async (
    caseNumber: string,
    extraRecords: Record<string, any>[] = []
  ): Promise<Record<string, any> | null> => {
    const trimmed = String(caseNumber || '').trim()
    const localCase = loadedCaseRecords(extraRecords).find(
      record => String(record.gm_id) === trimmed
    )
    if (localCase) {
      return localCase
    }
    const { data: caseData } = await getHeadlineDataByCase(trimmed)
    return normalizeHeadlineCaseData(caseData)
  }

  const persistRef2OverridesForNodes = (nodes: TreeDataNode[]) => {
    const nextRunOverrides = { ...ref2OverridesRef.current }
    const nextCaseOverrides = { ...ref2CaseOverridesRef.current }

    nodes.forEach(node => {
      const ref2Value = node[REF_GM_ID_2ND]
      const hasValue = ref2Value !== null && ref2Value !== undefined && ref2Value !== ''

      if (node.treeLevel === TREE_LEVEL_RUN && node.id) {
        const runId = String(node.id)
        if (hasValue) {
          nextRunOverrides[runId] = String(ref2Value)
        } else {
          delete nextRunOverrides[runId]
        }
        return
      }

      if (node.treeLevel === TREE_LEVEL_CASE && node.gm_id) {
        const gmId = String(node.gm_id)
        if (hasValue && node[REF2_SOURCE] === 'case') {
          nextCaseOverrides[gmId] = String(ref2Value)
        } else {
          delete nextCaseOverrides[gmId]
        }
      }
    })

    ref2OverridesRef.current = nextRunOverrides
    ref2CaseOverridesRef.current = nextCaseOverrides
    writeRef2OverridesToStorage(nextRunOverrides, currentTeam)
    writeRef2OverridesToStorage(nextCaseOverrides, currentTeam, REF2_CASE_STORAGE_KEY_PREFIX)
  }

  const applyDraftingPairs = async (records: Record<string, any>[]) => {
    const caseNumbers = Array.from(
      new Set(records.map(record => String(record.gm_id || '')).filter(Boolean))
    )
    if (caseNumbers.length === 0) {
      return records
    }

    try {
      const draftingRes = (await getHeadlineDraftingInfo({
        cases: caseNumbers.join(',')
      } as any)) as any
      return annotateDraftingPairs(records, draftingRes?.data || [])
    } catch (error) {
      // Drafting badges are supplemental; never block the grid on this lookup.
      console.error('Failed to load drafting pair info:', error)
      return records
    }
  }

  const applySavedRef2Overrides = async (records: Record<string, any>[]) => {
    const runOverrides = ref2OverridesRef.current
    const caseOverrides = ref2CaseOverridesRef.current
    if (Object.keys(runOverrides).length === 0 && Object.keys(caseOverrides).length === 0) {
      return records
    }

    const unique = (values: (string | undefined)[]) =>
      Array.from(new Set(values.filter(Boolean))) as string[]
    const runTokens = unique(records.map(record => runOverrides[runKeyForRecord(record)]))
    const caseRefIds = unique(records.map(record => caseOverrides[String(record.gm_id)]))

    const referenceRuns = new Map<string, ReferenceRun>()
    const referenceCases = new Map<string, Record<string, any>>()
    await Promise.all([
      ...runTokens.map(async token => {
        try {
          const referenceRun = await resolveReferenceRun(token, records)
          if (referenceRun) {
            referenceRuns.set(token, referenceRun)
          }
        } catch (error) {
          console.error(`Failed to restore persisted Ref 2 run ${token}:`, error)
        }
      }),
      ...caseRefIds.map(async caseRefId => {
        try {
          const referenceCase = await resolveReferenceCase(caseRefId, records)
          if (referenceCase) {
            referenceCases.set(caseRefId, referenceCase)
          }
        } catch (error) {
          console.error(`Failed to restore persisted Ref 2 case ${caseRefId}:`, error)
        }
      })
    ])

    return records.map(record => {
      const caseRefId = caseOverrides[String(record.gm_id)]
      if (caseRefId) {
        return applyRef2ToCaseRecord(
          record,
          caseRefId,
          referenceCases.get(caseRefId) || null,
          'case'
        )
      }

      const runToken = runOverrides[runKeyForRecord(record)]
      const referenceCase = runToken
        ? referenceRuns.get(runToken)?.casesByState.get(String(record.state_name))
        : undefined
      if (referenceCase && String(referenceCase.gm_id) !== String(record.gm_id)) {
        return applyRef2ToCaseRecord(record, String(referenceCase.gm_id), referenceCase, 'run')
      }

      return record
    })
  }

  const refreshGridSoon = (api: CellValueChangedEvent['api']) => {
    setTimeout(() => {
      api.refreshCells({ force: true })
    }, 100)
  }

  const restoreRef2Cell = (event: CellValueChangedEvent) => {
    // Restore old value directly in the data model without triggering a change event
    event.data[REF_GM_ID_2ND] = event.oldValue || null
    event.api.refreshCells({ rowNodes: [event.node], columns: [REF_GM_ID_2ND], force: true })
  }

  const updateRef2Nodes = (mapNode: (node: TreeDataNode) => TreeDataNode) => {
    setDataNodes(prevNodes => {
      const nextNodes = prevNodes.map(mapNode)
      persistRef2OverridesForNodes(nextNodes)
      return recalculateRunLevelAverages(nextNodes)
    })
  }

  const handleRunRef2Change = async (event: CellValueChangedEvent) => {
    const { newValue, data: rowData } = event
    const runId = String(rowData.id)
    const token = newValue === null || newValue === undefined ? '' : String(newValue).trim()
    const isChildCaseOfRun = (node: TreeDataNode) =>
      node.treeLevel === TREE_LEVEL_CASE && String(node.parentId) === runId

    if (!token) {
      // Clearing the reference run also clears any per-case Ref 2 entries in the run.
      updateRef2Nodes(node => {
        if (node.id === runId) {
          return { ...node, [REF_GM_ID_2ND]: null }
        }
        return isChildCaseOfRun(node) ? applyRef2ToCaseRecord(node, null, null) : node
      })
      refreshGridSoon(event.api)
      toast.success('Reference run cleared.')
      return
    }

    try {
      setIsLoading2ndRef(true)
      const referenceRun = await resolveReferenceRun(token)
      if (!referenceRun) {
        toast.error(
          `Reference run "${token}" not found. Enter a run name or any case number from the reference run.`
        )
        restoreRef2Cell(event)
        return
      }
      if (referenceRun.key === runId) {
        toast.error('Reference run cannot be the same run.')
        restoreRef2Cell(event)
        return
      }

      const unmatchedStates = dataNodesRef.current
        .filter(isChildCaseOfRun)
        .map(node => String(node.state_name))
        .filter(state => !referenceRun.casesByState.has(state))

      // Setting a reference run re-references every case in the run, replacing per-case entries.
      updateRef2Nodes(node => {
        if (node.id === runId) {
          return { ...node, [REF_GM_ID_2ND]: token }
        }
        if (!isChildCaseOfRun(node)) {
          return node
        }
        const referenceCase = referenceRun.casesByState.get(String(node.state_name))
        return referenceCase
          ? applyRef2ToCaseRecord(node, String(referenceCase.gm_id), referenceCase, 'run')
          : applyRef2ToCaseRecord(node, null, null)
      })
      refreshGridSoon(event.api)

      if (unmatchedStates.length > 0) {
        toast(`Reference run updated. No matching state for: ${unmatchedStates.join(', ')}`)
      } else {
        toast.success('Reference run updated.')
      }
    } catch (error) {
      console.error('Error updating reference run:', error)
      toast.error('Failed to update reference run.')
      restoreRef2Cell(event)
    } finally {
      setIsLoading2ndRef(false)
    }
  }

  const handleCaseRef2Change = async (event: CellValueChangedEvent) => {
    const { newValue, data: rowData } = event
    const caseNodeId = String(rowData.id)
    const caseRefId = newValue === null || newValue === undefined ? '' : String(newValue).trim()

    try {
      setIsLoading2ndRef(true)

      if (!caseRefId) {
        // Fall back to the run's reference run, if one is set.
        const runToken = ref2OverridesRef.current[String(rowData.parentId)]
        let fallbackCase: Record<string, any> | undefined
        if (runToken) {
          try {
            const referenceRun = await resolveReferenceRun(runToken)
            fallbackCase = referenceRun?.casesByState.get(String(rowData.state_name))
          } catch (error) {
            console.error('Failed to resolve run reference while clearing case Ref 2:', error)
          }
        }
        if (fallbackCase && String(fallbackCase.gm_id) === String(rowData.gm_id)) {
          fallbackCase = undefined
        }

        updateRef2Nodes(node => {
          if (String(node.id) !== caseNodeId) {
            return node
          }
          return fallbackCase
            ? applyRef2ToCaseRecord(node, String(fallbackCase.gm_id), fallbackCase, 'run')
            : applyRef2ToCaseRecord(node, null, null)
        })
        refreshGridSoon(event.api)
        toast.success(
          fallbackCase
            ? "Case reference cleared; using the run's reference run."
            : 'Temp reference cleared.'
        )
        return
      }

      const referenceCase = await resolveReferenceCase(caseRefId)
      if (!referenceCase) {
        toast.error(`Case ${caseRefId} not found.`)
        restoreRef2Cell(event)
        return
      }

      updateRef2Nodes(node =>
        String(node.id) === caseNodeId
          ? applyRef2ToCaseRecord(node, caseRefId, referenceCase, 'case')
          : node
      )
      refreshGridSoon(event.api)
      toast.success('Reference updated.')
    } catch (error) {
      console.error('Error updating reference case:', error)
      toast.error('Failed to update reference case.')
      restoreRef2Cell(event)
    } finally {
      setIsLoading2ndRef(false)
    }
  }

  const handleRef2Change = async (event: CellValueChangedEvent) => {
    if (event.colDef.field !== REF_GM_ID_2ND) {
      return
    }
    const level = event.data?.treeLevel
    if (level === TREE_LEVEL_RUN) {
      await handleRunRef2Change(event)
    } else if (level === TREE_LEVEL_CASE) {
      await handleCaseRef2Change(event)
    }
  }

  const externalFilterPass = useCallback(
    node => {
      // console.log('custom_state_filter--->all', node.data, refSelectedStates.current)
      if (
        (!refSelectedStates.current || refSelectedStates.current.length === 0) &&
        (!refSelectedProjects.current || refSelectedProjects.current.length === 0) &&
        (!refSelectedRuns.current || refSelectedRuns.current.length === 0) &&
        (!refSelectedBaselines.current || refSelectedBaselines.current.length === 0)
      ) {
        return false
      }
      if (!node.data) {
        return false
      }

      const selectedStates = new Set((refSelectedStates.current || []).map(item => String(item)))
      const selectedProjects = new Set(
        (refSelectedProjects.current || []).map(item => String(item))
      )
      const selectedBaselines = new Set(
        (refSelectedBaselines.current || []).map(item => String(item))
      )
      const selectedRuns = new Set((refSelectedRuns.current || []).map(item => String(item)))
      const hasStateFilter = selectedStates.size > 0
      const hasBaselineFilter = selectedBaselines.size > 0

      const isRunSelected = (runKey: string, runId?: string) => {
        if (selectedRuns.size === 0) {
          return false
        }
        if (selectedRuns.has(String(runKey))) {
          return true
        }
        if (runId) {
          return Array.from(selectedRuns).some(filterItem =>
            filterItem.endsWith(KEY_DELIMITER + runId)
          )
        }
        return false
      }

      const caseMatchesFilters = (caseRow: Record<string, any>) => {
        const caseIdParts = String(caseRow?.id || '').split(KEY_DELIMITER)
        const projectId = String(caseIdParts[0] ?? caseRow?.project ?? '')
        const runId = String(caseIdParts[1] ?? caseRow?.run ?? '')
        const caseId = String(caseIdParts[2] ?? caseRow?.state_name ?? caseRow?.name ?? '')
        const runKey = String(caseRow?.parentId || `${projectId}${KEY_DELIMITER}${runId}`)

        // no project selected or project not in selected list, filter out
        if (selectedProjects.size === 0 || !selectedProjects.has(projectId)) {
          return false
        }

        if (!isRunSelected(runKey, runId)) {
          return false
        }

        // if baseline filter exists, only show cases that belong to the selected baselines.
        // Note that baseline_run can be null/undefined for some old cases, those cases will
        // be filtered out when baseline filter is on to avoid confusion,
        // as we don't want to treat them as baseline or non-baseline cases.
        const baselineValue = caseRow?.baseline_run
        if (
          hasBaselineFilter &&
          (baselineValue === null ||
            baselineValue === undefined ||
            !selectedBaselines.has(String(baselineValue)))
        ) {
          return false
        }

        if (hasStateFilter && !selectedStates.has(caseId)) {
          return false
        }

        return true
      }

      const runHasVisibleCases = (runData: any) => {
        const runCases: Record<string, any>[] = runData?.cases || []
        if (!runCases || runCases.length === 0) {
          return false
        }

        return runCases.some(caseRow =>
          caseMatchesFilters({
            ...caseRow,
            parentId: runData?.id
          })
        )
      }

      if (node.data.treeLevel === TREE_LEVEL_PROJECT) {
        // console.log('custom_state_filter--->project', node.data, refSelectedProjects.current, refSelectedStates.current)
        // for project level, check if any of the runs contain the selected states
        if (
          !refSelectedProjects.current ||
          refSelectedProjects.current.length === 0 ||
          !selectedProjects.has(String(node.data.id))
        ) {
          return false
        }

        const projectPrefix = `${node.data.id}${KEY_DELIMITER}`
        return dataNodes.some(dataNode => {
          if (dataNode.treeLevel !== TREE_LEVEL_RUN) {
            return false
          }
          if (!String(dataNode.id).startsWith(projectPrefix)) {
            return false
          }
          const runId = String(dataNode.id).split(KEY_DELIMITER)[1]
          if (!isRunSelected(String(dataNode.id), runId)) {
            return false
          }
          return runHasVisibleCases(dataNode)
        })

        // const result = refSelectedStates.current.some(filterItem => value.stateChildren.includes(filterItem));
        // return result;
      } else if (node.data.treeLevel === TREE_LEVEL_RUN) {
        // for run level, check if the run contains the selected states
        // console.log('custom_state_filter--->run', node, node.data, refSelectedRuns.current, refSelectedStates.current)
        const idParts = node.data.id.split(KEY_DELIMITER)
        const projectId = idParts[0]
        const runId = idParts[1]

        // if no project is selected or the parent project is not selected, filter out the run
        // this is to ensure the project level filter works correctly, as AG Grid will not call
        // externalFilterPass for the child nodes if the parent node is filtered out
        if (
          !refSelectedProjects.current ||
          refSelectedProjects.current.length === 0 ||
          !selectedProjects.has(projectId)
        ) {
          return false
        }

        // if no run is selected or this run is not selected, filter out the run
        if (
          !refSelectedRuns.current ||
          refSelectedRuns.current.length === 0 ||
          !isRunSelected(node.data.id, runId)
        ) {
          return false
        }

        // Keep run rows only if at least one case is visible under current baseline/state filters.
        return runHasVisibleCases(node.data)
      } else if (node.data.treeLevel === TREE_LEVEL_CASE) {
        // for project level, check if any of the runs contain the selected states
        // console.log('custom_state_filter--->case', node.data, refSelectedStates.current)
        const idParts = node.data.id.split(KEY_DELIMITER)
        const projectId = idParts[0]
        const runId = idParts[1]
        const caseId = idParts[2]
        if (!refSelectedProjects.current || refSelectedProjects.current.length === 0) {
          // console.log('custom_state_filter--->case', 'no project selected', refSelectedProjects.current)
          return false
        }

        // if baseline is selected, only show cases that belong to the baseline or the original refcase (to allow user to see the delta values)
        if (
          refSelectedBaselines.current &&
          refSelectedBaselines.current.length > 0 &&
          !refSelectedBaselines.current.includes(String(node.data.baseline_run))
        ) {
          return false
        }

        return caseMatchesFilters({
          ...node.data,
          project: projectId,
          run: runId,
          state_name: caseId
        })
      } else {
        return false
      }
    },
    [dataNodes]
  )

  useEffect(() => {
    document.documentElement.style.setProperty('--ag-cell-widget-spacing', '4px')
    // Fetch coefficient data for CUP28 projects
    getCupProjectRunCoefficientData({})
      .then(res => {
        if (res.data && refCupProjectRunCoefficientData.current) {
          console.log('Fetched CUP28 project run coefficient data:', res.data)
          res.data.forEach((item: any) => {
            const key = `${item.project}${KEY_DELIMITER}${item.run}`
            refCupProjectRunCoefficientData.current[item.run] = item
          })
        }
      })
      .catch(err => {
        console.error('Failed to fetch CUP28 project run coefficient data:', err)
      })
  }, [])

  const loadHeadlineData = async (projects: string) => {
    try {
      // Fetch report data
      console.log('Reloading headline data for projects:', projects)
      const reportRes = (await getHeadlineData({
        page: 0,
        pageSize: 10000,
        projects: projects
      } as any)) as any
      const data = reportRes.data
      const cols = data.manifest.schema.columns
        .map(field => {
          let fieldSetting = availableFields[field.name]
          if (!fieldSetting) {
            fieldSetting = extraAvailableFields[field.name]
          }
          if (!fieldSetting) {
            return undefined
          }
          const isAuto =
            fieldSetting && (fieldSetting.width === 'auto' || fieldSetting.width === undefined)
          const colWidthClass = isAuto ? '!w-auto' : '!min-w-[' + fieldSetting.width + 'px]'
          const baseHeaderClass = `!pt-2 px-1 ${colWidthClass} ${RIGHT_ALIGNED_HEADER_CLASS}`

          return {
            headerName: fieldSetting.headerName,
            field: field.name,
            // Store position in context for row mapping (not a valid AG Grid colDef property)
            context: { position: field.position },
            sortable: true,
            width: isAuto ? undefined : fieldSetting.width,
            flex: isAuto ? 1 : 0,
            resizable: fieldSetting.resizable,
            plottable: fieldSetting.plottable,
            // headerComponent: CustomCdHeader,
            suppressHeaderMenuButton: true,
            headerClass: params => {
              const fieldName = params?.colDef?.field
              const isSelected = fieldName === refXAxis.current || fieldName === refYAxis.current
              return isSelected
                ? `!bg-gm-aqua-light text-white ${baseHeaderClass}`
                : baseHeaderClass
            },
            cellClass: params => {
              const base = 'text-sm !pl-1 !py-[2px]'
              // Case (gm_id) and Ref (ref_gm_id) cells open the gallery — show a
              // hover affordance so users know they're clickable.
              const fieldName = params?.colDef?.field
              const isGalleryLink =
                params.data?.treeLevel === TREE_LEVEL_CASE &&
                ((fieldName === 'gm_id' && params.data?.gm_id) ||
                  (fieldName === 'ref_gm_id' && params.data?.ref_gm_id))
              return isGalleryLink
                ? `${base} cursor-pointer hover:underline hover:!text-gm-blue`
                : base
            },
            // cellClassRules (not a cellClass function) so AG Grid re-evaluates the
            // strikethrough on refreshCells when a cell is excluded/included from Δ AVG.
            cellClassRules: {
              'headline-delta-avg-excluded': (params: any) => {
                if (params.data?.treeLevel !== TREE_LEVEL_CASE) {
                  return false
                }
                const gmId = String(params.data?.gm_id ?? '')
                const exclusions = deltaAvgExclusionsRef.current
                // Excluding the whole case strikes through every averaged cell in that row.
                if (
                  (RUN_LEVEL_AVERAGE_FIELDS as readonly string[]).includes(field.name) &&
                  isRowDeltaAvgExcluded(exclusions, gmId)
                ) {
                  return true
                }
                return isCellDeltaAvgExcluded(exclusions, gmId, field.name)
              }
            },
            cellRendererParams: {
              padding: 'px-1'
            },
            cellRenderer: (rowData: any) => {
              const fieldName = rowData.colDef.field
              // console.log('Rendering cell for field:', field.name, 'with row data:', rowData)
              const fieldValue = rowData.value
              const level = rowData.data['treeLevel']
              if (fieldName.startsWith('delta_')) {
                // Don't show deltas for case-level rows that have no reference case
                if (
                  level === TREE_LEVEL_CASE &&
                  !rowData.data['ref_gm_id'] &&
                  !rowData.data[REF_GM_ID_2ND]
                ) {
                  return null
                }
                // Check raw fieldValue before Number() — Number(null) === 0 which is a false positive
                if (fieldValue === null || fieldValue === undefined) {
                  return null
                }
                const value = Number(fieldValue)
                if (isNaN(value)) {
                  return null
                }
                // delta_pct_clf should not have color coding and should display as percentage
                if (fieldName === 'delta_pct_clf') {
                  return <span className="text-sm">{value.toFixed(2)}%</span>
                }
                let valueColor = ''
                if (value === 0.0) {
                  valueColor = 'text-black dark:!text-neutral-60'
                } else if (Math.abs(value) < 0.001) {
                  valueColor = 'text-neutral-60 dark:!text-neutral-60'
                } else if (value < 0) {
                  valueColor = 'text-green-600'
                } else if (value > 0) {
                  valueColor = 'text-red-600'
                }

                return (
                  <span className={classNames('text-sm', valueColor)} title={String(value)}>
                    {value.toFixed(3)}
                  </span>
                )
              } else if (level === TREE_LEVEL_RUN) {
                // Run completion date mirrors the latest case completion.
                if (fieldName === 'end_time') {
                  if (fieldValue === null || fieldValue === undefined) {
                    return null
                  }
                  return fieldSetting.formatter
                    ? fieldSetting.formatter(fieldValue, level)
                    : fieldValue
                }
                if (rowData.data['clrun'] !== null && rowData.data['clrun'] !== undefined) {
                  // CUP28 coefficient data exists for this run, use the suffix to get coefficient data
                  if (['cd', 'cl', 'cs', 'clf', 'clr', 'pct_clf'].includes(fieldName)) {
                    const cellValue = rowData.data[`${fieldName}run`]
                    return cellDataFormatter(cellValue)
                  } else if (!HEAT_RUN_AVERAGE_FIELDS.has(fieldName)) {
                    return null
                  }
                  // Heat fields fall through to the run-average renderer below.
                }
                if (
                  ['cd', 'cl', 'cs', 'csf', 'csr', 'clf', 'clr', 'pct_clf', 'vr'].includes(
                    fieldName
                  )
                ) {
                  return cellDataFormatter(fieldValue)
                }
                if (HEAT_RUN_AVERAGE_FIELDS.has(fieldName)) {
                  if (fieldValue === null || fieldValue === undefined) {
                    return null
                  }
                  const number = Number(fieldValue)
                  if (isNaN(number)) {
                    return null
                  }
                  return fieldSetting.formatter ? fieldSetting.formatter(number, level) : number
                }
                return null
              } else {
                if (fieldValue === null || fieldValue === undefined) {
                  return null
                }
                if (fieldName === 'end_time') {
                  if (fieldSetting.formatter) {
                    return fieldSetting.formatter(fieldValue, level)
                  }
                  return fieldValue
                } else {
                  const number = Number(fieldValue)
                  if (isNaN(number)) {
                    return fieldValue
                  }
                  return fieldSetting.formatter ? fieldSetting.formatter(number, level) : number
                }
              }
            }
          }
        })
        .filter(col => col !== undefined) as any[]
      const refGmIdIndex = cols.findIndex(col => col.field === 'ref_gm_id')

      if (refGmIdIndex >= 0) {
        const field = { name: REF_GM_ID_2ND, position: cols.length }
        let fieldSetting = availableFields[field.name]
        const isAuto =
          fieldSetting && (fieldSetting.width === 'auto' || fieldSetting.width === undefined)
        const refGmId2ndCol = {
          headerName: fieldSetting.headerName,
          field: field.name,
          // Store position in context for row mapping (not a valid AG Grid colDef property)
          context: { position: field.position },
          sortable: true,
          editable: params => {
            const level = params.data?.treeLevel
            return level === TREE_LEVEL_RUN || level === TREE_LEVEL_CASE
          },
          valueSetter: p => {
            const next = p.newValue
            if (next === p.oldValue) return false
            // Clearing the cell removes the 2nd reference; skip the "same as" checks
            // (Number('') === 0 would otherwise collide with a blank value).
            if (next === null || next === undefined || String(next).trim() === '') {
              p.data[REF_GM_ID_2ND] = null
              return true
            }
            const trimmed = String(next).trim()
            if (p.data?.treeLevel === TREE_LEVEL_RUN) {
              // Run rows take a reference run: a run name or any case number from that run.
              const runCases = Array.isArray(p.data?.cases) ? p.data.cases : []
              if (runCases.some(c => String(c?.gm_id) === trimmed)) {
                toast.error('Reference run cannot be the same run')
                return false
              }
            } else {
              if (!/^\d+$/.test(trimmed)) {
                toast.error('Ref 2 must be a case number')
                return false
              }
              if (Number(p.data['gm_id']) === Number(trimmed)) {
                toast.error('Ref ID cannot be the same as ID')
                return false
              }
            }
            p.data[REF_GM_ID_2ND] = trimmed
            return true
          },
          width: isAuto ? undefined : fieldSetting.width,
          flex: isAuto ? 1 : 0,
          resizable: fieldSetting.resizable,
          suppressHeaderMenuButton: true,
          headerClass: `!pt-2 px-1 !w-[${
            isAuto ? 'auto' : fieldSetting.width + 'px'
          }] ${RIGHT_ALIGNED_HEADER_CLASS}`,
          cellClass: params =>
            classNames('text-sm !pb-[3px]', {
              'headline-ref2-editable-cell rounded-sm my-[3px]':
                params.data?.treeLevel === TREE_LEVEL_RUN ||
                params.data?.treeLevel === TREE_LEVEL_CASE,
              // Manual per-case entries stand out from values inherited from the reference run.
              'font-semibold':
                params.data?.treeLevel === TREE_LEVEL_CASE && params.data?.[REF2_SOURCE] === 'case'
            }),
          cellStyle: params =>
            params.data?.treeLevel === TREE_LEVEL_RUN || params.data?.treeLevel === TREE_LEVEL_CASE
              ? { cursor: 'text' }
              : undefined,
          cellRendererParams: {
            padding: 'px-1'
          },
          cellRenderer: (rowData: any) => {
            // console.log('Rendering cell for field:', field.name, 'with row data:', rowData)
            const fieldValue = rowData.value
            const level = rowData.data['treeLevel']
            // Editable on run rows (reference run) and case rows (per-case reference).
            if (
              (level !== TREE_LEVEL_RUN && level !== TREE_LEVEL_CASE) ||
              fieldValue === null ||
              fieldValue === undefined
            ) {
              return <div className="!w-full">&nbsp;</div>
            }
            const number = Number(fieldValue)
            if (isNaN(number)) {
              return fieldValue
            }
            return fieldSetting.formatter ? fieldSetting.formatter(number, level) : number
          }
        }
        cols.splice(refGmIdIndex + 1, 0, refGmId2ndCol)
      }

      // console.log('Processed columns---->:', cols)

      if (data.manifest.total_row_count > 0) {
        const ts = new Date().getTime()
        const dataArray = data.result.data_array.map((row, index) => {
          const rowObj = {
            index: ts + index
          } as Record<string, any>
          cols.forEach(col => {
            rowObj[col.field] = row[col.context?.position]
          })
          // console.log('Processing row:', rowObj)
          return rowObj
        })
        const hydratedDataArray = await applySavedRef2Overrides(dataArray)
        const annotatedDataArray = await applyDraftingPairs(hydratedDataArray)
        // console.log('Normalized data array:', dataArray)
        const treeData = createDataTree(
          annotatedDataArray,
          attitudeStateOrder,
          refCupProjectRunCoefficientData.current
        )
        // Mirror each run's persisted Ref 2 onto its run node so the editable cell shows it.
        treeData.forEach(node => {
          if (node.treeLevel === TREE_LEVEL_RUN) {
            const savedRef2 = ref2OverridesRef.current[String(node.id)]
            if (savedRef2) {
              node[REF_GM_ID_2ND] = savedRef2
            }
          }
        })
        // console.log('tree data-->', treeData)
        // const newTreeData = [...treeData]
        setDataNodes(prevNodes => recalculateRunLevelAverages([...prevNodes, ...treeData]))
        setRawHeadlineData(annotatedDataArray)
        console.log('Raw headline data:', annotatedDataArray)
      } else {
        console.log('No data found.')
        setDataNodes([])
        setRawHeadlineData([])
      }

      console.log('Available fields--->:', cols)
      // set columns only once
      if (columns.length === 0) {
        const finalCols = cols.filter(col => availableFields[col.field]) as any[]
        finalCols.sort((a, b) => {
          const aSetting = availableFields[a.field]
          const bSetting = availableFields[b.field]
          return aSetting.sortNumber - bSetting.sortNumber
        })

        // not showing baseline_run column for now
        setColumns(finalCols.filter(col => col.field !== 'baseline_run'))
      }
    } catch (error) {
      console.error('Error fetching headline data:', error)
      toast.error('Error fetching headline data')
    }
  }

  const handleProjectRunSelectionChange = (
    projectRunSelection: Record<string, boolean>,
    projects: string[],
    projectSelectionChanged: boolean,
    runs: string[],
    runSelectionChanged: boolean,
    newProjSelection: string[]
  ) => {
    console.log(
      'Project/Run selection changed:',
      projectRunSelection,
      projects,
      runs,
      projectSelectionChanged,
      runSelectionChanged
    )
    refProjectRunSelection.current = projectRunSelection
    refSelectedProjects.current = projects
    refSelectedRuns.current = runs
    if (newProjSelection && newProjSelection.length > 0) {
      const projToLoad = newProjSelection.filter(proj => {
        return (
          dataNodes.findIndex(node => node.treeLevel === TREE_LEVEL_PROJECT && node.id === proj) < 0
        )
      })
      projToLoad.length > 0 && loadHeadlineData(projToLoad.join(','))
    }
    setDataNodes(prevNodes => recalculateRunLevelAverages(prevNodes))
    gridRef.current && gridRef.current.api && gridRef.current.api.onFilterChanged()
  }

  /**
   * Handle row click in AG Grid
   * For case-level rows, show the post-proc images gallery
   */
  const handleRowClicked = useCallback((event: any) => {
    console.log('Row clicked:', event)
    const rowData = event.data
    if (!rowData) return

    // Only handle case-level rows (not project or run level)
    if (rowData.treeLevel === TREE_LEVEL_CASE) {
      // Extract case info from the row data
      // ID format: "project::run::state_name"
      const idParts = rowData.id.split(KEY_DELIMITER)
      const runName = idParts[1] || ''
      const stateName = idParts[2] || rowData.name || ''
      const gmId = rowData.gm_id

      // For systemRunId, we need to construct it from the run name
      // The run name format is typically like "CFD_2026_SSW_Sequence_C_0"
      // and gm_id is the case number (e.g., 60)
      // systemRunId format: "0-{gm_id}" (based on existing code patterns)
      const systemRunId = `0-${gmId}`

      console.log('📷 Case row clicked:', {
        id: rowData.id,
        gmId,
        runName,
        stateName,
        systemRunId
      })

      const caseEntry = {
        systemRunId,
        runId: runName,
        caseName: gmId,
        caseId: rowData.id,
        baselineRunId: rowData.ref_gm_id ? String(rowData.ref_gm_id) : undefined,
        ref2RunId: rowData[REF_GM_ID_2ND] ? String(rowData[REF_GM_ID_2ND]) : undefined
      }

      setSyncedIndex(null)
      setSelectedCases(prev => {
        // If already displayed, do nothing — panels are only closed via the close button
        const alreadySelected = prev.some(c => c.caseId === rowData.id)
        if (alreadySelected) {
          return prev
        }
        // Not yet selected → add normally, replacing the oldest if already 2 panels open
        if (prev.length >= 2) {
          return [prev[1], caseEntry]
        }
        return [...prev, caseEntry]
      })
      setSyncedIndex(null)
    }
  }, [])

  // Open the post-proc gallery for a case row. Pass a ref column field to open the referenced
  // case's gallery instead of the row's own case.
  const openInGallery = useCallback((rowData: any, columnField?: string) => {
    if (!rowData) return

    let caseEntry: {
      systemRunId: string
      runId: string
      caseName: string
      caseId: string
      baselineRunId?: string
      ref2RunId?: string
    }

    if (columnField === 'ref_gm_id' || columnField === REF_GM_ID_2ND) {
      const refGmId = String(rowData[columnField] || '')
      if (!refGmId) {
        toast.error('No reference case for this row.')
        return
      }
      caseEntry = {
        systemRunId: `0-${refGmId}`,
        runId: columnField === 'ref_gm_id' ? 'Ref' : 'Ref 2',
        caseName: refGmId,
        caseId: `ref::${refGmId}::${columnField}`
      }
    } else {
      const gmId = rowData.gm_id
      if (!gmId) {
        toast.error('GM ID not found for this case.')
        return
      }
      const idParts = String(rowData.id || '').split(KEY_DELIMITER)
      const runName = idParts[1] || rowData.run || ''
      caseEntry = {
        systemRunId: `0-${gmId}`,
        runId: runName,
        caseName: gmId,
        caseId: rowData.id,
        baselineRunId: rowData.ref_gm_id ? String(rowData.ref_gm_id) : undefined,
        ref2RunId: rowData[REF_GM_ID_2ND] ? String(rowData[REF_GM_ID_2ND]) : undefined
      }
    }

    setSyncedIndex(null)
    setSelectedCases(prev => {
      const alreadySelected = prev.some(c => c.caseId === caseEntry.caseId)
      if (alreadySelected) {
        return prev
      }
      if (prev.length >= 2) {
        return [prev[1], caseEntry]
      }
      return [...prev, caseEntry]
    })
  }, [])

  // Stable callback for fullscreen requests
  const handleRequestFullscreen = useCallback((idx: number) => {
    setFullscreenGalleryIndex(idx)
  }, [])

  // Stable callbacks for each gallery's image changes
  const handleGallery0ImageChange = useCallback((img: any, allImgs: any[]) => {
    setGalleryImages(prev => {
      const updated = [...prev]
      updated[0] = { currentImage: img, allImages: allImgs }
      return updated
    })
  }, [])

  const handleGallery1ImageChange = useCallback((img: any, allImgs: any[]) => {
    setGalleryImages(prev => {
      const updated = [...prev]
      updated[1] = { currentImage: img, allImages: allImgs }
      return updated
    })
  }, [])

  const openCaseInMav = async (rowData: any, gmIdOverride?: string) => {
    const gmId = gmIdOverride || String(rowData?.gm_id || '')
    if (!gmId) {
      toast.error('GM ID not found for this case.')
      return
    }

    let runName = ''
    let stateName = ''
    console.log(
      '[openCaseInMav] Opening case in MAV with gmId:',
      gmId,
      'rowData:',
      rowData,
      gmIdOverride
    )
    if (gmIdOverride) {
      // for ref_gm_id or ref_gm_id_2nd column, we need to fetch the run/state from API
      try {
        const { data } = await getRunCase(gmId as any)
        runName = String(data?.run || '')
        stateName = String(data?.state_name || '')
      } catch (error) {
        console.error(`Failed to resolve run/state by case number ${gmId}:`, error)
        toast.error(
          `Failed to resolve run/state by ref case number ${gmId}. Please check the case number.`
        )
        return
      }
    } else {
      const idParts = String(rowData?.id || '').split(KEY_DELIMITER)
      runName = idParts[1] || rowData?.run || 'Run'
      stateName = idParts[2] || rowData?.state_name || rowData?.name || gmId
    }

    const cases: Array<Record<string, unknown>> = []

    // When opening a main case, include its reference case(s) first so MAV loads them for
    // comparison, matching the "Open in MAV" behavior on post-processing images.
    if (!gmIdOverride) {
      const refIds = Array.from(
        new Set(
          [String(rowData?.ref_gm_id || ''), String(rowData?.[REF_GM_ID_2ND] || '')].filter(
            id => id && id !== gmId
          )
        )
      )

      for (const refId of refIds) {
        let refRunName = ''
        let refStateName = ''
        try {
          const { data } = await getRunCase(refId as any)
          refRunName = String(data?.run || '')
          refStateName = String(data?.state_name || '')
        } catch (error) {
          console.error(`Failed to resolve run/state for reference case ${refId}:`, error)
        }
        cases.push({
          ID: refId,
          Name: refRunName && refStateName ? `${refRunName} - ${refStateName}` : `Ref ${refId}`,
          IsReference: true,
          DataPath: ''
        })
      }
    }

    cases.push({
      ID: gmId,
      Name: `${runName} - ${stateName}`,
      IsReference: false,
      DataPath: ''
    })

    const param = {
      Name: `Session ${gmId}`,
      Description: `${runName} - ${stateName}`,
      LocalStoragePath: '',
      NetworkStoragePath: '',
      Cases: cases
    }

    window.location.href = `mav-launcher://${encodeURIComponent(JSON.stringify(param))}`
  }

  const goToCaseInSubmission = useCallback(
    (rowData: any) => {
      const gmId = String(rowData?.gm_id || '')
      if (!gmId) {
        toast.error('GM ID not found for this case.')
        return
      }

      const idParts = String(rowData?.id || '').split(KEY_DELIMITER)
      const runName = idParts[1] || rowData?.run || ''
      if (!runName) {
        toast.error('Run name not found for this case.')
        return
      }

      const caseId = `${gmId}`
      navigate(`/submissions/${encodeURIComponent(runName)}?case=0-${encodeURIComponent(caseId)}`)
    },
    [navigate]
  )

  const goToRunInSubmission = useCallback(
    (rowData: any) => {
      const idParts = String(rowData?.id || '').split(KEY_DELIMITER)
      const runName = idParts[1] || rowData?.run || rowData?.name || ''
      if (!runName) {
        toast.error('Run name not found.')
        return
      }

      navigate(`/submissions/${encodeURIComponent(runName)}`)
    },
    [navigate]
  )

  // Collect a run node's case rows and hand them to the Report Generator tab.
  // Build a run selection (project + run + cases with deltas) from a run node.
  const buildRunSelection = useCallback(
    (runNode: any): { project: string; runName: string; cases: any[] } | null => {
      const rowData = runNode?.data
      const idParts = String(rowData?.id || '').split(KEY_DELIMITER)
      const project = rowData?.project || idParts[0] || ''
      const runName = idParts[1] || rowData?.run || rowData?.name || ''

      const num = (v: any): number | undefined => {
        const n = typeof v === 'number' ? v : parseFloat(v)
        return Number.isFinite(n) ? n : undefined
      }
      // Delta = case coefficient − reference coefficient (Headline Data's definition).
      const deltaPairs: [string, string][] = [
        ['delta_cd', 'cd'],
        ['delta_cl', 'cl'],
        ['delta_clf', 'clf'],
        ['delta_clr', 'clr'],
        ['delta_csf', 'csf'],
        ['delta_csr', 'csr'],
        ['delta_pct_clf', 'pct_clf']
      ]
      // Index every loaded case by gm_id so references can be resolved for the delta math.
      const byGm = new Map<string, any>()
      gridRef.current?.api?.forEachNode(n => {
        const d = n?.data
        if (d && d.treeLevel === TREE_LEVEL_CASE && d.gm_id != null) {
          byGm.set(String(d.gm_id), d)
        }
      })

      const leaves = (runNode?.allLeafChildren || []) as any[]
      const runCases = leaves
        .map(n => n?.data)
        .filter(d => d && d.treeLevel === TREE_LEVEL_CASE && d.gm_id != null)
        .map(d => {
          const ref = d.ref_gm_id != null ? byGm.get(String(d.ref_gm_id)) : undefined
          const entry: any = {
            gmId: String(d.gm_id),
            systemRunId: `0-${d.gm_id}`,
            stateName: d.state_name ? String(d.state_name) : undefined,
            refGmId: d.ref_gm_id != null ? String(d.ref_gm_id) : undefined,
            yaw: num(d.yaw),
            steer: num(d.steer),
            roll: num(d.roll),
            speed: num(d.speed)
          }
          deltaPairs.forEach(([deltaKey, coeffKey]) => {
            const a = num(d[coeffKey])
            const b = ref ? num(ref[coeffKey]) : undefined
            entry[deltaKey] = a != null && b != null ? a - b : num(d[deltaKey])
          })
          return entry
        })
      if (!runCases.length) return null
      return { project, runName, cases: runCases }
    },
    []
  )

  const handleGenerateReport = useCallback(
    (runNode: any) => {
      if (!onGenerateReport) return
      const sel = buildRunSelection(runNode)
      if (!sel) {
        toast.error('No cases found for this run.')
        return
      }
      onGenerateReport({ ...sel, token: Date.now() })
    },
    [onGenerateReport, buildRunSelection]
  )

  const handleGoToMedia = useCallback(
    (runNode: any) => {
      if (!onGoToMedia) return
      const sel = buildRunSelection(runNode)
      if (!sel) {
        toast.error('No cases found for this run.')
        return
      }
      onGoToMedia({ ...sel, token: Date.now() })
    },
    [onGoToMedia, buildRunSelection]
  )

  const getContextMenuItems = useCallback(
    (params: ContextMenuParams) => {
      const node = params?.node
      const rowData = node?.data
      const level = rowData?.treeLevel
      const defaultItems = params?.defaultItems || []

      if (!rowData) {
        return defaultItems
      }

      const customItems: any[] = []

      if (level === TREE_LEVEL_CASE) {
        const columnField = params.column?.getColId?.() || ''
        const isRefColumn = columnField === 'ref_gm_id' || columnField === REF_GM_ID_2ND
        const refGmId = isRefColumn ? String(rowData[columnField] || '') : undefined
        customItems.push({
          name: 'Open in MAV',
          icon: '<span class="pi pi-external-link" style="margin-right: 6px;"></span>',
          action: event => {
            console.log(
              'Context menu action: Open in MAV for rowData:',
              rowData,
              'refGmId:',
              refGmId,
              event
            )
            openCaseInMav(rowData, refGmId)
          }
        })

        // Dashboard-only users may only launch MAV; navigation to submissions and
        // coefficient edits are outside their allowed scope.
        if (!isDashboardOnly) {
          customItems.push({
            name: 'Go to Case',
            icon: '<span class="pi pi-arrow-right" style="margin-right: 6px;"></span>',
            action: () => goToCaseInSubmission(rowData)
          })
        }

        // Offer "Open in Gallery" for the Case, Ref, and Ref 2 columns (ref columns need a value)
        const canOpenGallery =
          columnField === 'gm_id' ? !!rowData.gm_id : isRefColumn ? !!rowData[columnField] : false
        if (canOpenGallery) {
          customItems.push({
            name: 'Open in Gallery',
            icon: '<span class="pi pi-images" style="margin-right: 6px;"></span>',
            action: () => openInGallery(rowData, isRefColumn ? columnField : undefined)
          })
        }

        if (!isDashboardOnly && userCanMutate && CUP28States.includes(rowData.state_name)) {
          if (
            refCupProjectRunCoefficientData.current &&
            refCupProjectRunCoefficientData.current[rowData.run]
          ) {
            const cup28Run = refCupProjectRunCoefficientData.current[rowData.run]
            if (cup28Run) {
              // exists, check if the state is excluded or not
              if (cup28Run['excluded_states']?.includes(rowData.state_name)) {
                // already excluded
                customItems.push({
                  name: 'Include in Coefficient Calculation',
                  icon: '<span class="pi pi-check-circle" style="margin-right: 6px;"></span>',
                  action: () =>
                    recalculateCoefficient(rowData.project, rowData.run, rowData.state_name, '-')
                })
              } else {
                // not excluded yet
                customItems.push({
                  name: 'Exclude from Coefficient Calculation',
                  icon: '<span class="pi pi-minus-circle" style="margin-right: 6px;"></span>',
                  action: () =>
                    recalculateCoefficient(rowData.project, rowData.run, rowData.state_name, '+')
                })
              }
            }
          }
        }

        // Δ AVG include/exclude. Not offered for CUP28 states, which use the coefficient
        // calculation exclusion above instead.
        if (userCanMutate && !CUP28States.includes(rowData.state_name)) {
          const gmId = String(rowData.gm_id ?? '')
          const isGroupColumn = columnField === 'ag-Grid-AutoColumn'
          const isAverageField = (RUN_LEVEL_AVERAGE_FIELDS as readonly string[]).includes(
            columnField
          )
          if (gmId && isGroupColumn) {
            // row level Δ AVG include/exclude
            const excluded = isRowDeltaAvgExcluded(deltaAvgExclusionsRef.current, gmId)
            customItems.push({
              name: excluded ? 'Include in Δ AVG Calculation' : 'Exclude from Δ AVG Calculation',
              icon: excluded
                ? '<span class="pi pi-check-circle" style="margin-right: 6px;"></span>'
                : '<span class="pi pi-minus-circle" style="margin-right: 6px;"></span>',
              action: () => toggleRowDeltaAvgExclusion(rowData)
            })
          } else if (
            gmId &&
            isAverageField &&
            !isRowDeltaAvgExcluded(deltaAvgExclusionsRef.current, gmId)
          ) {
            // cell level Δ AVG include/exclude — hidden once the whole row is excluded,
            // since row-level exclusion already covers every cell.
            const excluded = isCellDeltaAvgExcluded(
              deltaAvgExclusionsRef.current,
              gmId,
              columnField
            )
            customItems.push({
              name: excluded ? 'Include in Δ AVG Calculation' : 'Exclude from Δ AVG Calculation',
              icon: excluded
                ? '<span class="pi pi-check-circle" style="margin-right: 6px;"></span>'
                : '<span class="pi pi-minus-circle" style="margin-right: 6px;"></span>',
              action: () => toggleCellDeltaAvgExclusion(rowData, columnField)
            })
          }
        }
      }

      if (level === TREE_LEVEL_RUN) {
        if (!isDashboardOnly) {
          customItems.push({
            name: 'Go to Run',
            icon: '<span class="pi pi-share-alt" style="margin-right: 6px;"></span>',
            action: () => goToRunInSubmission(rowData)
          })
        }
        if (onGenerateReport) {
          customItems.push({
            name: 'Open Report',
            tooltip: 'Open the Report Generator for this run',
            icon: '<span class="pi pi-file" style="margin-right: 6px;"></span>',
            action: () => handleGenerateReport(node)
          })
        }
        if (onGoToMedia) {
          customItems.push({
            name: 'Go To Media',
            tooltip: "Open this run's media library",
            icon: '<span class="pi pi-images" style="margin-right: 6px;"></span>',
            action: () => handleGoToMedia(node)
          })
        }
      }

      if (customItems.length === 0) {
        return defaultItems
      }

      return [...customItems, 'separator', ...defaultItems]
    },
    [
      goToCaseInSubmission,
      goToRunInSubmission,
      openCaseInMav,
      openInGallery,
      isDashboardOnly,
      userCanMutate,
      onGenerateReport,
      handleGenerateReport,
      onGoToMedia,
      handleGoToMedia,
      toggleRowDeltaAvgExclusion,
      toggleCellDeltaAvgExclusion
    ]
  )

  const exportVisibleGridAsImage = async () => {
    const gridContainer = gridContainerRef.current
    if (!gridContainer) {
      toast.error('Grid is not ready to export.')
      return
    }

    setExportingGridImage(true)
    try {
      const timestamp = new Date()
      const exportTimestamp = [
        timestamp.getFullYear(),
        String(timestamp.getMonth() + 1).padStart(2, '0'),
        String(timestamp.getDate()).padStart(2, '0')
      ].join('-')
      const exportTime = [
        String(timestamp.getHours()).padStart(2, '0'),
        String(timestamp.getMinutes()).padStart(2, '0'),
        String(timestamp.getSeconds()).padStart(2, '0')
      ].join('-')
      const downloadLink = document.createElement('a')
      downloadLink.download = `headline-data-${exportTimestamp}_${exportTime}.png`
      downloadLink.href = await toPng(gridContainer, {
        cacheBust: true,
        pixelRatio: 2
      })
      downloadLink.click()
    } catch (error) {
      console.error('Failed to export visible headline data grid:', error)
      toast.error('Failed to export the visible grid.')
    } finally {
      setExportingGridImage(false)
    }
  }

  return (
    <div className="flex flex-col h-full relative">
      {/* Offset clears the tab bar so the actions align with the "Welcome" header row. */}
      <div className="flex items-center space-x-2 absolute right-0 -top-[92px] z-10">
        <Button
          type="button"
          aria-label="MAV-Aero"
          tooltip="MAV-Aero"
          tooltipOptions={{ position: 'bottom' }}
          onClick={() => navigate('/mav-aero')}
          icon={
            <img src={mavAeroIcon} alt="MAV-Aero" className="!w-[18px] !h-[18px] object-contain" />
          }
          className="!w-8 !h-8 !p-0 justify-center bg-white dark:bg-neutral-90 border border-gm-blue dark:border-light-blue hover:bg-gm-blue/10 dark:hover:bg-white/10"
          pt={{
            label: {
              className: '!hidden'
            }
          }}
        />
        <Button
          type="button"
          aria-label="Export to Excel"
          tooltip="Export to Excel"
          tooltipOptions={{ position: 'bottom' }}
          icon="pi pi-download text-white"
          className="!w-8 !h-8 !p-0 justify-center bg-gm-aqua-light border border-gm-aqua-light hover:bg-gm-aqua-dark disabled:opacity-50"
          onClick={() => {
            gridRef.current?.api.exportDataAsExcel({
              processCellCallback: params => {
                const draftRole = params.node?.data?.[DRAFT_ROLE_FIELD]
                if (draftRole && params.column.getColId() === 'ag-Grid-AutoColumn') {
                  return `${params.value} | ${draftRole}`
                }
                return params.value
              }
            })
          }}
          pt={{
            label: {
              className: '!hidden'
            }
          }}></Button>

        <Button
          type="button"
          aria-label="Export Visible PNG"
          tooltip="Export Visible PNG"
          tooltipOptions={{ position: 'bottom' }}
          icon="pi pi-image text-white"
          className="!w-8 !h-8 !p-0 justify-center bg-gm-aqua-light border border-gm-aqua-light hover:bg-gm-aqua-dark disabled:opacity-50"
          disabled={exportingGridImage}
          loading={exportingGridImage}
          onClick={exportVisibleGridAsImage}
          pt={{
            label: {
              className: '!hidden'
            }
          }}
        />

        <Button
          type="button"
          aria-label="Show/Hide Columns"
          tooltip="Show/Hide Columns"
          tooltipOptions={{ position: 'bottom' }}
          icon="pi pi-cog text-white"
          className="!w-8 !h-8 !p-0 justify-center bg-gm-aqua-light border border-gm-aqua-light hover:bg-gm-aqua-dark"
          pt={{
            label: {
              className: '!hidden'
            }
          }}
          onClick={event => {
            columnsVisibilityOverlayRef.current && columnsVisibilityOverlayRef.current.toggle(event)
          }}
        />
        <OverlayPanel
          dismissable={true}
          showCloseIcon={false}
          ref={columnsVisibilityOverlayRef}
          className="w-64 p-2 px-0">
          <div className="flex flex-col items-stretch w-full">
            <div className="font-semibold mb-2 flex items-center justify-between">
              <span className="text-lg">Columns</span>
              <button
                type="button"
                className="border-none bg-transparent"
                onClick={() => {
                  columnsVisibilityOverlayRef.current && columnsVisibilityOverlayRef.current.hide()
                }}>
                <i className="pi pi-times text-neutral-70"></i>
              </button>
            </div>
            <ul className="flex flex-col max-h-[600px] w-full overflow-y-auto">
              {groupedColumnsVisibility.map(group => {
                const groupAllVisible = group.cols.every(col => col.visible)
                return (
                  <li key={group.name} className="flex flex-col">
                    {group.name && (
                      <div className="sticky top-0 z-10 flex items-center justify-between bg-neutral-10 dark:bg-neutral-80 border-b border-neutral-20 dark:border-neutral-70 px-2 py-1 mt-2">
                        <span className="text-xs font-bold uppercase tracking-wide text-neutral-90 dark:text-neutral-10">
                          {group.name}
                        </span>
                        <InputSwitch
                          checked={groupAllVisible}
                          onChange={e => {
                            const groupFields = new Set(group.cols.map(c => c.field))
                            setColumnsVisibility(prev => {
                              const updated = prev.map(c =>
                                groupFields.has(c.field) ? { ...c, visible: e.value } : c
                              )
                              localStorage.setItem(
                                HEADLINE_COLUMNS_VISIBILITY,
                                JSON.stringify(updated)
                              )
                              return updated
                            })
                          }}
                          pt={{
                            root: {
                              className: '!h-5 w-10'
                            },
                            slider: {
                              className: 'h-5 dark:!bg-neutral-10'
                            }
                          }}
                        />
                      </div>
                    )}
                    {group.cols.map(col => (
                      <div key={col.field} className="flex items-center justify-between py-1 pr-2">
                        <span>{col.label}</span>
                        <InputSwitch
                          checked={col.visible}
                          onChange={e => {
                            setColumnsVisibility(prev => {
                              const updated = prev.map(c =>
                                c.field === col.field ? { ...c, visible: e.value } : c
                              )
                              localStorage.setItem(
                                HEADLINE_COLUMNS_VISIBILITY,
                                JSON.stringify(updated)
                              )
                              return updated
                            })
                          }}
                          pt={{
                            root: {
                              className: '!h-5 w-10'
                            },
                            slider: {
                              className: 'h-5 dark:!bg-neutral-10'
                            }
                          }}
                        />
                      </div>
                    ))}
                  </li>
                )
              })}
            </ul>
          </div>
        </OverlayPanel>
      </div>
      <div className="flex items-stretch space-x-2 flex-1 min-h-0">
        {/* Collapsed panel keeps its height in the flow — the grid has no height of its own. */}
        <div
          aria-hidden={panelCollapsed}
          className={`flex flex-col items-stretch space-y-2 pb-2 pt-0 ${
            panelCollapsed ? 'w-0 overflow-hidden pointer-events-none' : ''
          }`}>
          <div className="flex items-center space-x-1">
            <div className="!basis-0 !grow">
              <Button
                label=""
                className="min-w-full flex items-center justify-between"
                onClick={() => {
                  onNewPlot({
                    projectRunSelection: refProjectRunSelection.current,
                    states: refSelectedStates.current,
                    baselines: refSelectedBaselines.current
                  })
                }}
                pt={{
                  root: {
                    className: 'basis-0 grow'
                  }
                }}
                style={{ flexGrow: 1 }}>
                <div className="flex items-center">
                  <BarChartIcon className="w-4 h-4 fill-white stroke-white mr-1" />
                  <span>Plot Data</span>
                </div>
                <Badge
                  value={dataCount}
                  className="h-[16px] leading-tight pt-[2px] dark:bg-neutral-70"></Badge>
              </Button>
            </div>
            {showFilter && (
              <button
                className="border-none flex items-center justify-center"
                onClick={() => {
                  setShowFilter(false)
                }}>
                <i className="pi pi-times w-5 h-5" />
              </button>
            )}
            {!showFilter && (
              <button
                className="border-none flex items-center justify-center"
                onClick={() => {
                  setShowFilter(true)
                }}>
                <FilterIcon2 className="w-5 h-5 fill-gm-blue" />
              </button>
            )}
          </div>

          <ProjectRunSelector
            key={`${currentTeam || 'unknown-team'}:${urlPreloadKey || 'default'}`}
            loadingHeadlineData={loadingHeadlineData || loadingRefRun}
            showFilter={showFilter}
            showAreaFilter={true}
            showCarFilter={true}
            topSpacing={50}
            initialProjectRunSelection={urlProjectRunSelection}
            initialRunId={urlCaseId ? '' : urlRunId}
            initialCaseId={urlCaseId}
            baselineSourceData={rawHeadlineData}
            filterContent={
              <div className="flex flex-col items-stretch gap-2">
                <InputText
                  value={gridSearchText}
                  onChange={e => handleGridSearch(e.target.value)}
                  placeholder="Enter run name or description to search"
                  className="w-full !py-1"
                />
                <div className="flex items-center gap-2">
                  <span>Sort:</span>
                  <SplitButton
                    label="Completion Date"
                    icon={completionSortOrder === 'desc' ? 'pi pi-arrow-down ' : 'pi pi-arrow-up '}
                    model={completionSortItems}
                    onClick={() => sortByCompletionDate(completionSortOrder)}
                    severity="secondary"
                    size="small"
                    outlined
                    appendTo="self"
                    menuStyle={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
                    pt={{
                      root: {
                        className: '!bg-transparent !border !border-solid border-neutral-70 '
                      },
                      button: {
                        root: { className: '!bg-transparent !text-neutral-70' },
                        label: { className: 'text-neutral-70 dark:text-neutral-50' }
                      },
                      icon: {
                        className: '!text-neutral-70'
                      },
                      menuButton: {
                        root: { className: '!bg-transparent' }
                      }
                    }}
                  />
                </div>
              </div>
            }
            onProjectRunSelectionChanged={handleProjectRunSelectionChange}
            onFilterLoadingChanged={(loadingFilter: boolean) => {
              setIsLoadingFilter(loadingFilter)
            }}
            onStateSelectionChanged={(states, reload) => {
              // console.log('State selection changed:', states)
              refSelectedStates.current = states
              if (reload) {
                loadHeadlineData('')
              }
              setDataNodes(prevNodes => recalculateRunLevelAverages(prevNodes))
              gridRef.current && gridRef.current.api && gridRef.current.api.onFilterChanged()
            }}
            onBaselineSelectionChanged={(baselines, reload) => {
              refSelectedBaselines.current = baselines
              if (reload) {
                loadHeadlineData('')
              }
              setDataNodes(prevNodes => recalculateRunLevelAverages(prevNodes))
              gridRef.current && gridRef.current.api && gridRef.current.api.onFilterChanged()
            }}
            onReset={() => {
              refSelectedProjects.current = []
              refSelectedRuns.current = []
              refSelectedStates.current = []
              refSelectedBaselines.current = []
              setDataNodes([])
            }}
            onInitialProjectRunSelectionRejected={(selectionType, value) => {
              const label = selectionType === 'runId' ? 'Run' : 'Case'
              toast.error(
                `${label} ${value || urlCaseId || urlRunId} is not available to your account.`
              )
            }}
          />
        </div>
        <button
          className="shrink-0 w-5 flex flex-col items-center pt-2 hover:bg-blue-500/10 transition-colors border-r border-solid border-neutral-200 dark:border-neutral-600"
          onClick={() => setPanelCollapsed(collapsed => !collapsed)}
          title={panelCollapsed ? 'Expand panel' : 'Collapse panel'}
          aria-label={panelCollapsed ? 'Expand panel' : 'Collapse panel'}>
          <i
            className={`pi ${
              panelCollapsed ? 'pi-chevron-right' : 'pi-chevron-left'
            } text-[0.6rem] text-neutral-500 dark:text-neutral-400`}
          />
        </button>
        <div
          ref={gridContainerRef}
          className="flex flex-col items-stretch flex-1 min-w-0 px-2 overflow-hidden">
          <div className="shrink-0 py-2 flex items-center gap-2">
            <InputText
              value={gridSearchText}
              onChange={e => handleGridSearch(e.target.value)}
              placeholder="Enter run name or description to search"
              className="w-full md:w-[28rem] !py-1"
            />
            <span className="ml-4">Sort:</span>
            <SplitButton
              label="Completion Date"
              icon={completionSortOrder === 'desc' ? 'pi pi-arrow-down ' : 'pi pi-arrow-up '}
              model={completionSortItems}
              onClick={() => sortByCompletionDate(completionSortOrder)}
              severity="secondary"
              size="small"
              outlined
              appendTo="self"
              menuStyle={{ minWidth: 'max-content', whiteSpace: 'nowrap' }}
              pt={{
                root: {
                  className: '!bg-transparent !border !border-solid border-neutral-70 '
                },
                button: {
                  root: { className: '!bg-transparent !text-neutral-70' },
                  label: { className: 'text-neutral-70 dark:text-neutral-50' }
                },
                icon: {
                  className: '!text-neutral-70'
                },
                menuButton: {
                  root: { className: '!bg-transparent' }
                }
              }}
            />
          </div>
          <AgGridReact
            ref={gridRef}
            className="w-full flex-1 min-h-0"
            theme={gridTheme}
            rowData={dataNodes}
            treeDataParentIdField="parentId"
            treeData={true}
            columnDefs={columns}
            getRowId={getRowId}
            defaultColDef={defaultColDef}
            autoGroupColumnDef={autoGroupColumnDef}
            headerHeight={29}
            rowHeight={33}
            groupDefaultExpanded={-1}
            loading={
              loadingHeadlineData ||
              isLoadingFilter ||
              isLoading2ndRef ||
              loadingRefRun ||
              loadingCupProjectRunCoefficientData
            }
            excludeChildrenWhenTreeDataFiltering={true}
            getContextMenuItems={getContextMenuItems}
            onFirstDataRendered={() => {
              syncProjectExpansionState()
              sizeHeadlineGridColumns()
              // Default ordering: most recent completion date first.
              sortByCompletionDate('desc')
            }}
            onGridSizeChanged={sizeHeadlineGridColumns}
            onRowGroupOpened={syncProjectExpansionState}
            isExternalFilterPresent={() => {
              return true
            }}
            onFilterChanged={(evt: FilterChangedEvent) => {
              let caseCount = 0
              gridRef.current.api.forEachNodeAfterFilterAndSort(node => {
                if (!node.group && node.data) caseCount++
              })
              setDataCount(caseCount)
            }}
            noRowsOverlayComponent={() => (
              <div className="flex flex-col items-center justify-center space-y-2 text-gray-500 dark:text-gray-400">
                <i className="pi pi-inbox text-4xl"></i>
                <div className="text-lg font-medium ">No headline data available</div>
                <div className="text-sm">Select a project and run to view data</div>
              </div>
            )}
            doesExternalFilterPass={externalFilterPass}
            onRowClicked={(event: any) => {
              if (event.event?.defaultPrevented) {
                return
              }
              handleRowClicked(event)
            }}
            onCellValueChanged={handleRef2Change}
            onCellClicked={(evt: CellClickedEvent) => {
              const field = evt.colDef?.field
              const rowData = evt.data

              // Ref 2 is editable on run and case rows — open the cell editor on click so the value
              // can be changed or cleared (backspace/delete). Opening the image gallery here would
              // hijack the click and prevent editing.
              if (
                field === REF_GM_ID_2ND &&
                (rowData?.treeLevel === TREE_LEVEL_RUN || rowData?.treeLevel === TREE_LEVEL_CASE)
              ) {
                // Prevent the row-click handler from firing so the cell can enter edit mode
                evt.event?.preventDefault()
                if (evt.rowIndex != null) {
                  evt.api.startEditingCell({ rowIndex: evt.rowIndex, colKey: REF_GM_ID_2ND })
                }
                return
              }

              // Handle clicks on the read-only reference case column
              if (field === 'ref_gm_id' && rowData?.treeLevel === TREE_LEVEL_CASE) {
                const refGmId = rowData[field]

                // Only open gallery if the reference case has a value
                if (refGmId) {
                  evt.event?.preventDefault()
                  evt.event?.stopPropagation()

                  const systemRunId = `0-${refGmId}`
                  const caseEntry = {
                    systemRunId,
                    runId: 'Ref',
                    caseName: refGmId,
                    caseId: `ref::${refGmId}::${field}`
                  }

                  setSyncedIndex(null)
                  setSelectedCases(prev => {
                    const alreadySelected = prev.some(c => c.caseId === caseEntry.caseId)
                    if (alreadySelected) {
                      return prev
                    }
                    if (prev.length >= 2) {
                      return [prev[1], caseEntry]
                    }
                    return [...prev, caseEntry]
                  })
                }
              }
            }}
            rowSelection="single"
            rowClass="cursor-pointer"
            loadingOverlayComponent={() => {
              return (
                <div className="flex items-center justify-center space-x-2">
                  <i className="pi pi-spin pi-spinner text-xl text-gm-blue"></i>
                  <div>Loading...</div>
                </div>
              )
            }}
          />
        </div>
      </div>

      {/* Post-Processing Images Gallery Panel */}
      {galleryOpen && (
        <div
          ref={galleryPanelRef}
          className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden scroll-mb-4"
          style={{ height: '60vh', minHeight: '400px' }}>
          <div className="flex items-center justify-end gap-2 px-3 py-2 border-b border-gray-200 dark:border-gray-700">
            <Button
              label="Player"
              icon="pi pi-play"
              size="small"
              severity="secondary"
              outlined
              onClick={() => setShowGifPlayer(true)}
              className="!text-xs"
              pt={{
                label: { style: { color: '#ffffff' } },
                icon: { style: { color: '#ffffff', marginRight: '0.5rem' } }
              }}
            />
            {userCanMutate && (
              <Button
                label="Create GIF"
                icon="pi pi-images"
                size="small"
                onClick={() => setShowGifModal(true)}
                className="!text-xs"
                pt={{ icon: { style: { marginRight: '0.5rem' } } }}
              />
            )}
          </div>
          <div className="flex flex-1 min-h-0">
            {selectedCases.map((c, i) => (
              <div
                key={c.caseId}
                className={classNames('flex flex-col flex-1 min-w-0', {
                  'border-r border-gray-300 dark:border-gray-600':
                    i === 0 && selectedCases.length === 2
                })}>
                <PostProcGallery
                  systemRunId={c.systemRunId}
                  runId={c.runId}
                  caseName={c.caseName}
                  baselineRunId={c.baselineRunId}
                  ref2RunId={c.ref2RunId}
                  onClose={() => {
                    setSelectedCases(prev => prev.filter(x => x.caseId !== c.caseId))
                    setSyncedIndex(null)
                  }}
                  isExpanded={true}
                  syncedIndex={selectedCases.length === 2 ? syncedIndex : undefined}
                  onSyncedImageChange={selectedCases.length === 2 ? setSyncedIndex : undefined}
                  galleryIndex={i}
                  galleriesLinked={selectedCases.length === 2 ? galleriesLinked : undefined}
                  onToggleLink={
                    selectedCases.length === 2 ? () => setGalleriesLinked(prev => !prev) : undefined
                  }
                  onRequestFullscreen={handleRequestFullscreen}
                  onCurrentImageChange={
                    selectedCases.length === 2
                      ? i === 0
                        ? handleGallery0ImageChange
                        : handleGallery1ImageChange
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <CreateGifModal
        isOpen={showGifModal}
        onClose={() => setShowGifModal(false)}
        cases={selectedCases.map<GifCaseOption>(c => ({
          systemRunId: c.systemRunId,
          runId: c.runId,
          caseName: c.caseName,
          runName: c.caseName
        }))}
      />

      <GifPlayerScreen
        isOpen={showGifPlayer}
        onClose={() => setShowGifPlayer(false)}
        cases={selectedCases.map<GifCaseOption>(c => ({
          systemRunId: c.systemRunId,
          runId: c.runId,
          caseName: c.caseName,
          runName: c.caseName
        }))}
      />

      {/* Fullscreen Comparison Dialog */}
      {fullscreenGalleryIndex !== null && (
        <Dialog
          visible={true}
          onHide={() => setFullscreenGalleryIndex(null)}
          header={selectedCases[fullscreenGalleryIndex]?.caseName}
          style={{ width: '95vw', height: '95vh' }}
          maximizable
          modal
          pt={{
            content: { className: 'flex flex-col h-full p-4' }
          }}>
          <div className="flex flex-col items-center" style={{ height: 'calc(95vh - 120px)' }}>
            {/* Image display with navigation arrows */}
            <div className="relative flex-1 w-full flex items-center justify-center overflow-hidden">
              {galleryImages[fullscreenGalleryIndex]?.currentImage ? (
                <>
                  <img
                    src={galleryImages[fullscreenGalleryIndex].currentImage.url}
                    alt={galleryImages[fullscreenGalleryIndex].currentImage.name}
                    className="max-w-full max-h-full object-contain"
                  />
                  {/* Left arrow */}
                  {galleryImages[fullscreenGalleryIndex].allImages.length > 1 && (
                    <button
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3"
                      onClick={() => {
                        const currentIdx = galleryImages[
                          fullscreenGalleryIndex
                        ].allImages.findIndex(
                          img =>
                            img.fullPath ===
                            galleryImages[fullscreenGalleryIndex].currentImage.fullPath
                        )
                        const prevIdx =
                          currentIdx > 0
                            ? currentIdx - 1
                            : galleryImages[fullscreenGalleryIndex].allImages.length - 1
                        // Notify the gallery component to update its image
                        setSyncedIndex(prevIdx)
                      }}>
                      <i className="pi pi-chevron-left text-xl" />
                    </button>
                  )}
                  {/* Right arrow */}
                  {galleryImages[fullscreenGalleryIndex].allImages.length > 1 && (
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-3"
                      onClick={() => {
                        const currentIdx = galleryImages[
                          fullscreenGalleryIndex
                        ].allImages.findIndex(
                          img =>
                            img.fullPath ===
                            galleryImages[fullscreenGalleryIndex].currentImage.fullPath
                        )
                        const nextIdx =
                          currentIdx < galleryImages[fullscreenGalleryIndex].allImages.length - 1
                            ? currentIdx + 1
                            : 0
                        // Notify the gallery component to update its image
                        setSyncedIndex(nextIdx)
                      }}>
                      <i className="pi pi-chevron-right text-xl" />
                    </button>
                  )}
                </>
              ) : (
                <div className="text-gray-500">Loading image...</div>
              )}
            </div>

            {/* Controls - always visible */}
            <div className="mt-4 flex flex-wrap items-center justify-center gap-4 bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg flex-shrink-0">
              {/* Toggle between galleries */}
              <Button
                label={`Switch to ${selectedCases[fullscreenGalleryIndex === 0 ? 1 : 0]?.caseName}`}
                icon="pi pi-sync"
                iconPos="left"
                severity="info"
                onClick={() => setFullscreenGalleryIndex(fullscreenGalleryIndex === 0 ? 1 : 0)}
                pt={{
                  icon: { className: 'mr-2' }
                }}
              />

              {/* Image info */}
              {galleryImages[fullscreenGalleryIndex]?.currentImage && (
                <span className="text-sm text-gray-600 dark:text-gray-300">
                  {galleryImages[fullscreenGalleryIndex].allImages.findIndex(
                    img =>
                      img.fullPath === galleryImages[fullscreenGalleryIndex].currentImage.fullPath
                  ) + 1}{' '}
                  / {galleryImages[fullscreenGalleryIndex].allImages.length}
                </span>
              )}
            </div>

            {/* Image metadata */}
            {galleryImages[fullscreenGalleryIndex]?.currentImage?.metadata && (
              <div className="mt-2 flex flex-wrap justify-center gap-3 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                <span>{galleryImages[fullscreenGalleryIndex].currentImage.name}</span>
                {galleryImages[fullscreenGalleryIndex].currentImage.metadata.section && (
                  <>
                    <span>
                      Section: {galleryImages[fullscreenGalleryIndex].currentImage.metadata.section}
                    </span>
                    <span>
                      Type: {galleryImages[fullscreenGalleryIndex].currentImage.metadata.metricType}
                    </span>
                    <span>
                      Index: {galleryImages[fullscreenGalleryIndex].currentImage.metadata.index}
                    </span>
                    <span>
                      Value: {galleryImages[fullscreenGalleryIndex].currentImage.metadata.value}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </Dialog>
      )}
    </div>
  )
}

export default HeadlineData
